/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Te = window, gt = Te.ShadowRoot && (Te.ShadyCSS === void 0 || Te.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, yt = Symbol(), jt = /* @__PURE__ */ new WeakMap();
class wr {
  constructor(e, r, n) {
    if (this._$cssResult$ = !0, n !== yt)
      throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = r;
  }
  get styleSheet() {
    let e = this.o;
    const r = this.t;
    if (gt && e === void 0) {
      const n = r !== void 0 && r.length === 1;
      n && (e = jt.get(r)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), n && jt.set(r, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
}
const gn = (t) => new wr(typeof t == "string" ? t : t + "", void 0, yt), Pe = (t, ...e) => {
  const r = t.length === 1 ? t[0] : e.reduce((n, i, a) => n + ((s) => {
    if (s._$cssResult$ === !0)
      return s.cssText;
    if (typeof s == "number")
      return s;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + s + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(i) + t[a + 1], t[0]);
  return new wr(r, t, yt);
}, yn = (t, e) => {
  gt ? t.adoptedStyleSheets = e.map((r) => r instanceof CSSStyleSheet ? r : r.styleSheet) : e.forEach((r) => {
    const n = document.createElement("style"), i = Te.litNonce;
    i !== void 0 && n.setAttribute("nonce", i), n.textContent = r.cssText, t.appendChild(n);
  });
}, Ct = gt ? (t) => t : (t) => t instanceof CSSStyleSheet ? ((e) => {
  let r = "";
  for (const n of e.cssRules)
    r += n.cssText;
  return gn(r);
})(t) : t;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var ze;
const Ee = window, Ot = Ee.trustedTypes, _n = Ot ? Ot.emptyScript : "", Pt = Ee.reactiveElementPolyfillSupport, at = { toAttribute(t, e) {
  switch (e) {
    case Boolean:
      t = t ? _n : null;
      break;
    case Object:
    case Array:
      t = t == null ? t : JSON.stringify(t);
  }
  return t;
}, fromAttribute(t, e) {
  let r = t;
  switch (e) {
    case Boolean:
      r = t !== null;
      break;
    case Number:
      r = t === null ? null : Number(t);
      break;
    case Object:
    case Array:
      try {
        r = JSON.parse(t);
      } catch {
        r = null;
      }
  }
  return r;
} }, xr = (t, e) => e !== t && (e == e || t == t), Ke = { attribute: !0, type: String, converter: at, reflect: !1, hasChanged: xr };
class V extends HTMLElement {
  constructor() {
    super(), this._$Ei = /* @__PURE__ */ new Map(), this.isUpdatePending = !1, this.hasUpdated = !1, this._$El = null, this.u();
  }
  static addInitializer(e) {
    var r;
    (r = this.h) !== null && r !== void 0 || (this.h = []), this.h.push(e);
  }
  static get observedAttributes() {
    this.finalize();
    const e = [];
    return this.elementProperties.forEach((r, n) => {
      const i = this._$Ep(n, r);
      i !== void 0 && (this._$Ev.set(i, n), e.push(i));
    }), e;
  }
  static createProperty(e, r = Ke) {
    if (r.state && (r.attribute = !1), this.finalize(), this.elementProperties.set(e, r), !r.noAccessor && !this.prototype.hasOwnProperty(e)) {
      const n = typeof e == "symbol" ? Symbol() : "__" + e, i = this.getPropertyDescriptor(e, n, r);
      i !== void 0 && Object.defineProperty(this.prototype, e, i);
    }
  }
  static getPropertyDescriptor(e, r, n) {
    return { get() {
      return this[r];
    }, set(i) {
      const a = this[e];
      this[r] = i, this.requestUpdate(e, a, n);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) || Ke;
  }
  static finalize() {
    if (this.hasOwnProperty("finalized"))
      return !1;
    this.finalized = !0;
    const e = Object.getPrototypeOf(this);
    if (e.finalize(), this.elementProperties = new Map(e.elementProperties), this._$Ev = /* @__PURE__ */ new Map(), this.hasOwnProperty("properties")) {
      const r = this.properties, n = [...Object.getOwnPropertyNames(r), ...Object.getOwnPropertySymbols(r)];
      for (const i of n)
        this.createProperty(i, r[i]);
    }
    return this.elementStyles = this.finalizeStyles(this.styles), !0;
  }
  static finalizeStyles(e) {
    const r = [];
    if (Array.isArray(e)) {
      const n = new Set(e.flat(1 / 0).reverse());
      for (const i of n)
        r.unshift(Ct(i));
    } else
      e !== void 0 && r.push(Ct(e));
    return r;
  }
  static _$Ep(e, r) {
    const n = r.attribute;
    return n === !1 ? void 0 : typeof n == "string" ? n : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  u() {
    var e;
    this._$E_ = new Promise((r) => this.enableUpdating = r), this._$AL = /* @__PURE__ */ new Map(), this._$Eg(), this.requestUpdate(), (e = this.constructor.h) === null || e === void 0 || e.forEach((r) => r(this));
  }
  addController(e) {
    var r, n;
    ((r = this._$ES) !== null && r !== void 0 ? r : this._$ES = []).push(e), this.renderRoot !== void 0 && this.isConnected && ((n = e.hostConnected) === null || n === void 0 || n.call(e));
  }
  removeController(e) {
    var r;
    (r = this._$ES) === null || r === void 0 || r.splice(this._$ES.indexOf(e) >>> 0, 1);
  }
  _$Eg() {
    this.constructor.elementProperties.forEach((e, r) => {
      this.hasOwnProperty(r) && (this._$Ei.set(r, this[r]), delete this[r]);
    });
  }
  createRenderRoot() {
    var e;
    const r = (e = this.shadowRoot) !== null && e !== void 0 ? e : this.attachShadow(this.constructor.shadowRootOptions);
    return yn(r, this.constructor.elementStyles), r;
  }
  connectedCallback() {
    var e;
    this.renderRoot === void 0 && (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$ES) === null || e === void 0 || e.forEach((r) => {
      var n;
      return (n = r.hostConnected) === null || n === void 0 ? void 0 : n.call(r);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$ES) === null || e === void 0 || e.forEach((r) => {
      var n;
      return (n = r.hostDisconnected) === null || n === void 0 ? void 0 : n.call(r);
    });
  }
  attributeChangedCallback(e, r, n) {
    this._$AK(e, n);
  }
  _$EO(e, r, n = Ke) {
    var i;
    const a = this.constructor._$Ep(e, n);
    if (a !== void 0 && n.reflect === !0) {
      const s = (((i = n.converter) === null || i === void 0 ? void 0 : i.toAttribute) !== void 0 ? n.converter : at).toAttribute(r, n.type);
      this._$El = e, s == null ? this.removeAttribute(a) : this.setAttribute(a, s), this._$El = null;
    }
  }
  _$AK(e, r) {
    var n;
    const i = this.constructor, a = i._$Ev.get(e);
    if (a !== void 0 && this._$El !== a) {
      const s = i.getPropertyOptions(a), o = typeof s.converter == "function" ? { fromAttribute: s.converter } : ((n = s.converter) === null || n === void 0 ? void 0 : n.fromAttribute) !== void 0 ? s.converter : at;
      this._$El = a, this[a] = o.fromAttribute(r, s.type), this._$El = null;
    }
  }
  requestUpdate(e, r, n) {
    let i = !0;
    e !== void 0 && (((n = n || this.constructor.getPropertyOptions(e)).hasChanged || xr)(this[e], r) ? (this._$AL.has(e) || this._$AL.set(e, r), n.reflect === !0 && this._$El !== e && (this._$EC === void 0 && (this._$EC = /* @__PURE__ */ new Map()), this._$EC.set(e, n))) : i = !1), !this.isUpdatePending && i && (this._$E_ = this._$Ej());
  }
  async _$Ej() {
    this.isUpdatePending = !0;
    try {
      await this._$E_;
    } catch (r) {
      Promise.reject(r);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var e;
    if (!this.isUpdatePending)
      return;
    this.hasUpdated, this._$Ei && (this._$Ei.forEach((i, a) => this[a] = i), this._$Ei = void 0);
    let r = !1;
    const n = this._$AL;
    try {
      r = this.shouldUpdate(n), r ? (this.willUpdate(n), (e = this._$ES) === null || e === void 0 || e.forEach((i) => {
        var a;
        return (a = i.hostUpdate) === null || a === void 0 ? void 0 : a.call(i);
      }), this.update(n)) : this._$Ek();
    } catch (i) {
      throw r = !1, this._$Ek(), i;
    }
    r && this._$AE(n);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var r;
    (r = this._$ES) === null || r === void 0 || r.forEach((n) => {
      var i;
      return (i = n.hostUpdated) === null || i === void 0 ? void 0 : i.call(n);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$Ek() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$E_;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$EC !== void 0 && (this._$EC.forEach((r, n) => this._$EO(n, this[n], r)), this._$EC = void 0), this._$Ek();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
}
V.finalized = !0, V.elementProperties = /* @__PURE__ */ new Map(), V.elementStyles = [], V.shadowRootOptions = { mode: "open" }, Pt == null || Pt({ ReactiveElement: V }), ((ze = Ee.reactiveElementVersions) !== null && ze !== void 0 ? ze : Ee.reactiveElementVersions = []).push("1.4.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var Fe;
const Se = window, W = Se.trustedTypes, kt = W ? W.createPolicy("lit-html", { createHTML: (t) => t }) : void 0, M = `lit$${(Math.random() + "").slice(9)}$`, _t = "?" + M, $n = `<${_t}>`, Y = document, de = (t = "") => Y.createComment(t), he = (t) => t === null || typeof t != "object" && typeof t != "function", Tr = Array.isArray, Er = (t) => Tr(t) || typeof (t == null ? void 0 : t[Symbol.iterator]) == "function", ae = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, Mt = /-->/g, It = />/g, N = RegExp(`>|[ 	
\f\r](?:([^\\s"'>=/]+)([ 	
\f\r]*=[ 	
\f\r]*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), Ht = /'/g, Rt = /"/g, Sr = /^(?:script|style|textarea|title)$/i, mn = (t) => (e, ...r) => ({ _$litType$: t, strings: e, values: r }), S = mn(1), O = Symbol.for("lit-noChange"), _ = Symbol.for("lit-nothing"), Lt = /* @__PURE__ */ new WeakMap(), q = Y.createTreeWalker(Y, 129, null, !1), jr = (t, e) => {
  const r = t.length - 1, n = [];
  let i, a = e === 2 ? "<svg>" : "", s = ae;
  for (let c = 0; c < r; c++) {
    const l = t[c];
    let u, h, d = -1, p = 0;
    for (; p < l.length && (s.lastIndex = p, h = s.exec(l), h !== null); )
      p = s.lastIndex, s === ae ? h[1] === "!--" ? s = Mt : h[1] !== void 0 ? s = It : h[2] !== void 0 ? (Sr.test(h[2]) && (i = RegExp("</" + h[2], "g")), s = N) : h[3] !== void 0 && (s = N) : s === N ? h[0] === ">" ? (s = i != null ? i : ae, d = -1) : h[1] === void 0 ? d = -2 : (d = s.lastIndex - h[2].length, u = h[1], s = h[3] === void 0 ? N : h[3] === '"' ? Rt : Ht) : s === Rt || s === Ht ? s = N : s === Mt || s === It ? s = ae : (s = N, i = void 0);
    const f = s === N && t[c + 1].startsWith("/>") ? " " : "";
    a += s === ae ? l + $n : d >= 0 ? (n.push(u), l.slice(0, d) + "$lit$" + l.slice(d) + M + f) : l + M + (d === -2 ? (n.push(void 0), c) : f);
  }
  const o = a + (t[r] || "<?>") + (e === 2 ? "</svg>" : "");
  if (!Array.isArray(t) || !t.hasOwnProperty("raw"))
    throw Error("invalid template strings array");
  return [kt !== void 0 ? kt.createHTML(o) : o, n];
};
class ue {
  constructor({ strings: e, _$litType$: r }, n) {
    let i;
    this.parts = [];
    let a = 0, s = 0;
    const o = e.length - 1, c = this.parts, [l, u] = jr(e, r);
    if (this.el = ue.createElement(l, n), q.currentNode = this.el.content, r === 2) {
      const h = this.el.content, d = h.firstChild;
      d.remove(), h.append(...d.childNodes);
    }
    for (; (i = q.nextNode()) !== null && c.length < o; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes()) {
          const h = [];
          for (const d of i.getAttributeNames())
            if (d.endsWith("$lit$") || d.startsWith(M)) {
              const p = u[s++];
              if (h.push(d), p !== void 0) {
                const f = i.getAttribute(p.toLowerCase() + "$lit$").split(M), v = /([.?@])?(.*)/.exec(p);
                c.push({ type: 1, index: a, name: v[2], strings: f, ctor: v[1] === "." ? Or : v[1] === "?" ? Pr : v[1] === "@" ? kr : pe });
              } else
                c.push({ type: 6, index: a });
            }
          for (const d of h)
            i.removeAttribute(d);
        }
        if (Sr.test(i.tagName)) {
          const h = i.textContent.split(M), d = h.length - 1;
          if (d > 0) {
            i.textContent = W ? W.emptyScript : "";
            for (let p = 0; p < d; p++)
              i.append(h[p], de()), q.nextNode(), c.push({ type: 2, index: ++a });
            i.append(h[d], de());
          }
        }
      } else if (i.nodeType === 8)
        if (i.data === _t)
          c.push({ type: 2, index: a });
        else {
          let h = -1;
          for (; (h = i.data.indexOf(M, h + 1)) !== -1; )
            c.push({ type: 7, index: a }), h += M.length - 1;
        }
      a++;
    }
  }
  static createElement(e, r) {
    const n = Y.createElement("template");
    return n.innerHTML = e, n;
  }
}
function z(t, e, r = t, n) {
  var i, a, s, o;
  if (e === O)
    return e;
  let c = n !== void 0 ? (i = r._$Co) === null || i === void 0 ? void 0 : i[n] : r._$Cl;
  const l = he(e) ? void 0 : e._$litDirective$;
  return (c == null ? void 0 : c.constructor) !== l && ((a = c == null ? void 0 : c._$AO) === null || a === void 0 || a.call(c, !1), l === void 0 ? c = void 0 : (c = new l(t), c._$AT(t, r, n)), n !== void 0 ? ((s = (o = r)._$Co) !== null && s !== void 0 ? s : o._$Co = [])[n] = c : r._$Cl = c), c !== void 0 && (e = z(t, c._$AS(t, e.values), c, n)), e;
}
class Cr {
  constructor(e, r) {
    this.u = [], this._$AN = void 0, this._$AD = e, this._$AM = r;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  v(e) {
    var r;
    const { el: { content: n }, parts: i } = this._$AD, a = ((r = e == null ? void 0 : e.creationScope) !== null && r !== void 0 ? r : Y).importNode(n, !0);
    q.currentNode = a;
    let s = q.nextNode(), o = 0, c = 0, l = i[0];
    for (; l !== void 0; ) {
      if (o === l.index) {
        let u;
        l.type === 2 ? u = new X(s, s.nextSibling, this, e) : l.type === 1 ? u = new l.ctor(s, l.name, l.strings, this, e) : l.type === 6 && (u = new Mr(s, this, e)), this.u.push(u), l = i[++c];
      }
      o !== (l == null ? void 0 : l.index) && (s = q.nextNode(), o++);
    }
    return a;
  }
  p(e) {
    let r = 0;
    for (const n of this.u)
      n !== void 0 && (n.strings !== void 0 ? (n._$AI(e, n, r), r += n.strings.length - 2) : n._$AI(e[r])), r++;
  }
}
class X {
  constructor(e, r, n, i) {
    var a;
    this.type = 2, this._$AH = _, this._$AN = void 0, this._$AA = e, this._$AB = r, this._$AM = n, this.options = i, this._$Cm = (a = i == null ? void 0 : i.isConnected) === null || a === void 0 || a;
  }
  get _$AU() {
    var e, r;
    return (r = (e = this._$AM) === null || e === void 0 ? void 0 : e._$AU) !== null && r !== void 0 ? r : this._$Cm;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const r = this._$AM;
    return r !== void 0 && e.nodeType === 11 && (e = r.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, r = this) {
    e = z(this, e, r), he(e) ? e === _ || e == null || e === "" ? (this._$AH !== _ && this._$AR(), this._$AH = _) : e !== this._$AH && e !== O && this.g(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : Er(e) ? this.k(e) : this.g(e);
  }
  O(e, r = this._$AB) {
    return this._$AA.parentNode.insertBefore(e, r);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  g(e) {
    this._$AH !== _ && he(this._$AH) ? this._$AA.nextSibling.data = e : this.T(Y.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var r;
    const { values: n, _$litType$: i } = e, a = typeof i == "number" ? this._$AC(e) : (i.el === void 0 && (i.el = ue.createElement(i.h, this.options)), i);
    if (((r = this._$AH) === null || r === void 0 ? void 0 : r._$AD) === a)
      this._$AH.p(n);
    else {
      const s = new Cr(a, this), o = s.v(this.options);
      s.p(n), this.T(o), this._$AH = s;
    }
  }
  _$AC(e) {
    let r = Lt.get(e.strings);
    return r === void 0 && Lt.set(e.strings, r = new ue(e)), r;
  }
  k(e) {
    Tr(this._$AH) || (this._$AH = [], this._$AR());
    const r = this._$AH;
    let n, i = 0;
    for (const a of e)
      i === r.length ? r.push(n = new X(this.O(de()), this.O(de()), this, this.options)) : n = r[i], n._$AI(a), i++;
    i < r.length && (this._$AR(n && n._$AB.nextSibling, i), r.length = i);
  }
  _$AR(e = this._$AA.nextSibling, r) {
    var n;
    for ((n = this._$AP) === null || n === void 0 || n.call(this, !1, !0, r); e && e !== this._$AB; ) {
      const i = e.nextSibling;
      e.remove(), e = i;
    }
  }
  setConnected(e) {
    var r;
    this._$AM === void 0 && (this._$Cm = e, (r = this._$AP) === null || r === void 0 || r.call(this, e));
  }
}
class pe {
  constructor(e, r, n, i, a) {
    this.type = 1, this._$AH = _, this._$AN = void 0, this.element = e, this.name = r, this._$AM = i, this.options = a, n.length > 2 || n[0] !== "" || n[1] !== "" ? (this._$AH = Array(n.length - 1).fill(new String()), this.strings = n) : this._$AH = _;
  }
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e, r = this, n, i) {
    const a = this.strings;
    let s = !1;
    if (a === void 0)
      e = z(this, e, r, 0), s = !he(e) || e !== this._$AH && e !== O, s && (this._$AH = e);
    else {
      const o = e;
      let c, l;
      for (e = a[0], c = 0; c < a.length - 1; c++)
        l = z(this, o[n + c], r, c), l === O && (l = this._$AH[c]), s || (s = !he(l) || l !== this._$AH[c]), l === _ ? e = _ : e !== _ && (e += (l != null ? l : "") + a[c + 1]), this._$AH[c] = l;
    }
    s && !i && this.j(e);
  }
  j(e) {
    e === _ ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e != null ? e : "");
  }
}
class Or extends pe {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === _ ? void 0 : e;
  }
}
const bn = W ? W.emptyScript : "";
class Pr extends pe {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    e && e !== _ ? this.element.setAttribute(this.name, bn) : this.element.removeAttribute(this.name);
  }
}
class kr extends pe {
  constructor(e, r, n, i, a) {
    super(e, r, n, i, a), this.type = 5;
  }
  _$AI(e, r = this) {
    var n;
    if ((e = (n = z(this, e, r, 0)) !== null && n !== void 0 ? n : _) === O)
      return;
    const i = this._$AH, a = e === _ && i !== _ || e.capture !== i.capture || e.once !== i.once || e.passive !== i.passive, s = e !== _ && (i === _ || a);
    a && this.element.removeEventListener(this.name, this, i), s && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var r, n;
    typeof this._$AH == "function" ? this._$AH.call((n = (r = this.options) === null || r === void 0 ? void 0 : r.host) !== null && n !== void 0 ? n : this.element, e) : this._$AH.handleEvent(e);
  }
}
class Mr {
  constructor(e, r, n) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = r, this.options = n;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    z(this, e);
  }
}
const An = { P: "$lit$", A: M, M: _t, C: 1, L: jr, R: Cr, D: Er, V: z, I: X, H: pe, N: Pr, U: kr, B: Or, F: Mr }, Ut = Se.litHtmlPolyfillSupport;
Ut == null || Ut(ue, X), ((Fe = Se.litHtmlVersions) !== null && Fe !== void 0 ? Fe : Se.litHtmlVersions = []).push("2.4.0");
const wn = (t, e, r) => {
  var n, i;
  const a = (n = r == null ? void 0 : r.renderBefore) !== null && n !== void 0 ? n : e;
  let s = a._$litPart$;
  if (s === void 0) {
    const o = (i = r == null ? void 0 : r.renderBefore) !== null && i !== void 0 ? i : null;
    a._$litPart$ = s = new X(e.insertBefore(de(), o), o, void 0, r != null ? r : {});
  }
  return s._$AI(t), s;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var Ge, Ve;
class L extends V {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var e, r;
    const n = super.createRenderRoot();
    return (e = (r = this.renderOptions).renderBefore) !== null && e !== void 0 || (r.renderBefore = n.firstChild), n;
  }
  update(e) {
    const r = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = wn(r, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) === null || e === void 0 || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) === null || e === void 0 || e.setConnected(!1);
  }
  render() {
    return O;
  }
}
L.finalized = !0, L._$litElement$ = !0, (Ge = globalThis.litElementHydrateSupport) === null || Ge === void 0 || Ge.call(globalThis, { LitElement: L });
const Nt = globalThis.litElementPolyfillSupport;
Nt == null || Nt({ LitElement: L });
((Ve = globalThis.litElementVersions) !== null && Ve !== void 0 ? Ve : globalThis.litElementVersions = []).push("3.2.2");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ke = (t) => (e) => typeof e == "function" ? ((r, n) => (customElements.define(r, n), n))(t, e) : ((r, n) => {
  const { kind: i, elements: a } = n;
  return { kind: i, elements: a, finisher(s) {
    customElements.define(r, s);
  } };
})(t, e);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const xn = (t, e) => e.kind === "method" && e.descriptor && !("value" in e.descriptor) ? { ...e, finisher(r) {
  r.createProperty(e.key, t);
} } : { kind: "field", key: Symbol(), placement: "own", descriptor: {}, originalKey: e.key, initializer() {
  typeof e.initializer == "function" && (this[e.key] = e.initializer.call(this));
}, finisher(r) {
  r.createProperty(e.key, t);
} };
function w(t) {
  return (e, r) => r !== void 0 ? ((n, i, a) => {
    i.constructor.createProperty(a, n);
  })(t, e, r) : xn(t, e);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function U(t) {
  return w({ ...t, state: !0 });
}
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
var qe;
((qe = window.HTMLSlotElement) === null || qe === void 0 ? void 0 : qe.prototype.assignedElements) != null;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const P = { ATTRIBUTE: 1, CHILD: 2, PROPERTY: 3, BOOLEAN_ATTRIBUTE: 4, EVENT: 5, ELEMENT: 6 }, ve = (t) => (...e) => ({ _$litDirective$: t, values: e });
class ge {
  constructor(e) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(e, r, n) {
    this._$Ct = e, this._$AM = r, this._$Ci = n;
  }
  _$AS(e, r) {
    return this.update(e, r);
  }
  update(e, r) {
    return this.render(...r);
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { I: Tn } = An, Ir = (t) => t.strings === void 0, Dt = () => document.createComment(""), se = (t, e, r) => {
  var n;
  const i = t._$AA.parentNode, a = e === void 0 ? t._$AB : e._$AA;
  if (r === void 0) {
    const s = i.insertBefore(Dt(), a), o = i.insertBefore(Dt(), a);
    r = new Tn(s, o, t, t.options);
  } else {
    const s = r._$AB.nextSibling, o = r._$AM, c = o !== t;
    if (c) {
      let l;
      (n = r._$AQ) === null || n === void 0 || n.call(r, t), r._$AM = t, r._$AP !== void 0 && (l = t._$AU) !== o._$AU && r._$AP(l);
    }
    if (s !== a || c) {
      let l = r._$AA;
      for (; l !== s; ) {
        const u = l.nextSibling;
        i.insertBefore(l, a), l = u;
      }
    }
  }
  return r;
}, D = (t, e, r = t) => (t._$AI(e, r), t), En = {}, Hr = (t, e = En) => t._$AH = e, Sn = (t) => t._$AH, We = (t) => {
  var e;
  (e = t._$AP) === null || e === void 0 || e.call(t, !1, !0);
  let r = t._$AA;
  const n = t._$AB.nextSibling;
  for (; r !== n; ) {
    const i = r.nextSibling;
    r.remove(), r = i;
  }
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Bt = (t, e, r) => {
  const n = /* @__PURE__ */ new Map();
  for (let i = e; i <= r; i++)
    n.set(t[i], i);
  return n;
}, jn = ve(class extends ge {
  constructor(t) {
    if (super(t), t.type !== P.CHILD)
      throw Error("repeat() can only be used in text expressions");
  }
  ht(t, e, r) {
    let n;
    r === void 0 ? r = e : e !== void 0 && (n = e);
    const i = [], a = [];
    let s = 0;
    for (const o of t)
      i[s] = n ? n(o, s) : s, a[s] = r(o, s), s++;
    return { values: a, keys: i };
  }
  render(t, e, r) {
    return this.ht(t, e, r).values;
  }
  update(t, [e, r, n]) {
    var i;
    const a = Sn(t), { values: s, keys: o } = this.ht(e, r, n);
    if (!Array.isArray(a))
      return this.ut = o, s;
    const c = (i = this.ut) !== null && i !== void 0 ? i : this.ut = [], l = [];
    let u, h, d = 0, p = a.length - 1, f = 0, v = s.length - 1;
    for (; d <= p && f <= v; )
      if (a[d] === null)
        d++;
      else if (a[p] === null)
        p--;
      else if (c[d] === o[f])
        l[f] = D(a[d], s[f]), d++, f++;
      else if (c[p] === o[v])
        l[v] = D(a[p], s[v]), p--, v--;
      else if (c[d] === o[v])
        l[v] = D(a[d], s[v]), se(t, l[v + 1], a[d]), d++, v--;
      else if (c[p] === o[f])
        l[f] = D(a[p], s[f]), se(t, a[d], a[p]), p--, f++;
      else if (u === void 0 && (u = Bt(o, f, v), h = Bt(c, d, p)), u.has(c[d]))
        if (u.has(c[p])) {
          const m = h.get(o[f]), j = m !== void 0 ? a[m] : null;
          if (j === null) {
            const C = se(t, a[d]);
            D(C, s[f]), l[f] = C;
          } else
            l[f] = D(j, s[f]), se(t, a[d], j), a[m] = null;
          f++;
        } else
          We(a[p]), p--;
      else
        We(a[d]), d++;
    for (; f <= v; ) {
      const m = se(t, l[v + 1]);
      D(m, s[f]), l[f++] = m;
    }
    for (; d <= p; ) {
      const m = a[d++];
      m !== null && We(m);
    }
    return this.ut = o, Hr(t, l), O;
  }
});
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Cn = ve(class extends ge {
  constructor(t) {
    if (super(t), t.type !== P.PROPERTY && t.type !== P.ATTRIBUTE && t.type !== P.BOOLEAN_ATTRIBUTE)
      throw Error("The `live` directive is not allowed on child or event bindings");
    if (!Ir(t))
      throw Error("`live` bindings can only contain a single expression");
  }
  render(t) {
    return t;
  }
  update(t, [e]) {
    if (e === O || e === _)
      return e;
    const r = t.element, n = t.name;
    if (t.type === P.PROPERTY) {
      if (e === r[n])
        return O;
    } else if (t.type === P.BOOLEAN_ATTRIBUTE) {
      if (!!e === r.hasAttribute(n))
        return O;
    } else if (t.type === P.ATTRIBUTE && r.getAttribute(n) === e + "")
      return O;
    return Hr(t), e;
  }
});
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const le = (t, e) => {
  var r, n;
  const i = t._$AN;
  if (i === void 0)
    return !1;
  for (const a of i)
    (n = (r = a)._$AO) === null || n === void 0 || n.call(r, e, !1), le(a, e);
  return !0;
}, je = (t) => {
  let e, r;
  do {
    if ((e = t._$AM) === void 0)
      break;
    r = e._$AN, r.delete(t), t = e;
  } while ((r == null ? void 0 : r.size) === 0);
}, Rr = (t) => {
  for (let e; e = t._$AM; t = e) {
    let r = e._$AN;
    if (r === void 0)
      e._$AN = r = /* @__PURE__ */ new Set();
    else if (r.has(t))
      break;
    r.add(t), kn(e);
  }
};
function On(t) {
  this._$AN !== void 0 ? (je(this), this._$AM = t, Rr(this)) : this._$AM = t;
}
function Pn(t, e = !1, r = 0) {
  const n = this._$AH, i = this._$AN;
  if (i !== void 0 && i.size !== 0)
    if (e)
      if (Array.isArray(n))
        for (let a = r; a < n.length; a++)
          le(n[a], !1), je(n[a]);
      else
        n != null && (le(n, !1), je(n));
    else
      le(this, t);
}
const kn = (t) => {
  var e, r, n, i;
  t.type == P.CHILD && ((e = (n = t)._$AP) !== null && e !== void 0 || (n._$AP = Pn), (r = (i = t)._$AQ) !== null && r !== void 0 || (i._$AQ = On));
};
class Mn extends ge {
  constructor() {
    super(...arguments), this._$AN = void 0;
  }
  _$AT(e, r, n) {
    super._$AT(e, r, n), Rr(this), this.isConnected = e._$AU;
  }
  _$AO(e, r = !0) {
    var n, i;
    e !== this.isConnected && (this.isConnected = e, e ? (n = this.reconnected) === null || n === void 0 || n.call(this) : (i = this.disconnected) === null || i === void 0 || i.call(this)), r && (le(this, e), je(this));
  }
  setValue(e) {
    if (Ir(this._$Ct))
      this._$Ct._$AI(e, this);
    else {
      const r = [...this._$Ct._$AH];
      r[this._$Ci] = e, this._$Ct._$AI(r, this, 0);
    }
  }
  disconnected() {
  }
  reconnected() {
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Lr = () => new In();
class In {
}
const Ye = /* @__PURE__ */ new WeakMap(), Ur = ve(class extends Mn {
  render(t) {
    return _;
  }
  update(t, [e]) {
    var r;
    const n = e !== this.Y;
    return n && this.Y !== void 0 && this.rt(void 0), (n || this.lt !== this.ct) && (this.Y = e, this.dt = (r = t.options) === null || r === void 0 ? void 0 : r.host, this.rt(this.ct = t.element)), _;
  }
  rt(t) {
    var e;
    if (typeof this.Y == "function") {
      const r = (e = this.dt) !== null && e !== void 0 ? e : globalThis;
      let n = Ye.get(r);
      n === void 0 && (n = /* @__PURE__ */ new WeakMap(), Ye.set(r, n)), n.get(this.Y) !== void 0 && this.Y.call(this.dt, void 0), n.set(this.Y, t), t !== void 0 && this.Y.call(this.dt, t);
    } else
      this.Y.value = t;
  }
  get lt() {
    var t, e, r;
    return typeof this.Y == "function" ? (e = Ye.get((t = this.dt) !== null && t !== void 0 ? t : globalThis)) === null || e === void 0 ? void 0 : e.get(this.Y) : (r = this.Y) === null || r === void 0 ? void 0 : r.value;
  }
  disconnected() {
    this.lt === this.ct && this.rt(void 0);
  }
  reconnected() {
    this.rt(this.ct);
  }
});
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const st = ve(class extends ge {
  constructor(t) {
    var e;
    if (super(t), t.type !== P.ATTRIBUTE || t.name !== "class" || ((e = t.strings) === null || e === void 0 ? void 0 : e.length) > 2)
      throw Error("`classMap()` can only be used in the `class` attribute and must be the only part in the attribute.");
  }
  render(t) {
    return " " + Object.keys(t).filter((e) => t[e]).join(" ") + " ";
  }
  update(t, [e]) {
    var r, n;
    if (this.nt === void 0) {
      this.nt = /* @__PURE__ */ new Set(), t.strings !== void 0 && (this.st = new Set(t.strings.join(" ").split(/\s/).filter((a) => a !== "")));
      for (const a in e)
        e[a] && !(!((r = this.st) === null || r === void 0) && r.has(a)) && this.nt.add(a);
      return this.render(e);
    }
    const i = t.element.classList;
    this.nt.forEach((a) => {
      a in e || (i.remove(a), this.nt.delete(a));
    });
    for (const a in e) {
      const s = !!e[a];
      s === this.nt.has(a) || ((n = this.st) === null || n === void 0 ? void 0 : n.has(a)) || (s ? (i.add(a), this.nt.add(a)) : (i.remove(a), this.nt.delete(a)));
    }
    return O;
  }
});
/*!
 * hotkeys-js v3.8.7
 * A simple micro-library for defining and dispatching keyboard shortcuts. It has no dependencies.
 * 
 * Copyright (c) 2021 kenny wong <wowohoo@qq.com>
 * http://jaywcjlove.github.io/hotkeys
 * 
 * Licensed under the MIT license.
 */
var Je = typeof navigator < "u" ? navigator.userAgent.toLowerCase().indexOf("firefox") > 0 : !1;
function Xe(t, e, r) {
  t.addEventListener ? t.addEventListener(e, r, !1) : t.attachEvent && t.attachEvent("on".concat(e), function() {
    r(window.event);
  });
}
function Nr(t, e) {
  for (var r = e.slice(0, e.length - 1), n = 0; n < r.length; n++)
    r[n] = t[r[n].toLowerCase()];
  return r;
}
function Dr(t) {
  typeof t != "string" && (t = ""), t = t.replace(/\s/g, "");
  for (var e = t.split(","), r = e.lastIndexOf(""); r >= 0; )
    e[r - 1] += ",", e.splice(r, 1), r = e.lastIndexOf("");
  return e;
}
function Hn(t, e) {
  for (var r = t.length >= e.length ? t : e, n = t.length >= e.length ? e : t, i = !0, a = 0; a < r.length; a++)
    n.indexOf(r[a]) === -1 && (i = !1);
  return i;
}
var Br = {
  backspace: 8,
  tab: 9,
  clear: 12,
  enter: 13,
  return: 13,
  esc: 27,
  escape: 27,
  space: 32,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  del: 46,
  delete: 46,
  ins: 45,
  insert: 45,
  home: 36,
  end: 35,
  pageup: 33,
  pagedown: 34,
  capslock: 20,
  num_0: 96,
  num_1: 97,
  num_2: 98,
  num_3: 99,
  num_4: 100,
  num_5: 101,
  num_6: 102,
  num_7: 103,
  num_8: 104,
  num_9: 105,
  num_multiply: 106,
  num_add: 107,
  num_enter: 108,
  num_subtract: 109,
  num_decimal: 110,
  num_divide: 111,
  "\u21EA": 20,
  ",": 188,
  ".": 190,
  "/": 191,
  "`": 192,
  "-": Je ? 173 : 189,
  "=": Je ? 61 : 187,
  ";": Je ? 59 : 186,
  "'": 222,
  "[": 219,
  "]": 221,
  "\\": 220
}, K = {
  "\u21E7": 16,
  shift: 16,
  "\u2325": 18,
  alt: 18,
  option: 18,
  "\u2303": 17,
  ctrl: 17,
  control: 17,
  "\u2318": 91,
  cmd: 91,
  command: 91
}, zt = {
  16: "shiftKey",
  18: "altKey",
  17: "ctrlKey",
  91: "metaKey",
  shiftKey: 16,
  ctrlKey: 17,
  altKey: 18,
  metaKey: 91
}, E = {
  16: !1,
  18: !1,
  17: !1,
  91: !1
}, T = {};
for (var Ae = 1; Ae < 20; Ae++)
  Br["f".concat(Ae)] = 111 + Ae;
var y = [], zr = "all", Kr = [], Me = function(e) {
  return Br[e.toLowerCase()] || K[e.toLowerCase()] || e.toUpperCase().charCodeAt(0);
};
function Fr(t) {
  zr = t || "all";
}
function fe() {
  return zr || "all";
}
function Rn() {
  return y.slice(0);
}
function Ln(t) {
  var e = t.target || t.srcElement, r = e.tagName, n = !0;
  return (e.isContentEditable || (r === "INPUT" || r === "TEXTAREA" || r === "SELECT") && !e.readOnly) && (n = !1), n;
}
function Un(t) {
  return typeof t == "string" && (t = Me(t)), y.indexOf(t) !== -1;
}
function Nn(t, e) {
  var r, n;
  t || (t = fe());
  for (var i in T)
    if (Object.prototype.hasOwnProperty.call(T, i))
      for (r = T[i], n = 0; n < r.length; )
        r[n].scope === t ? r.splice(n, 1) : n++;
  fe() === t && Fr(e || "all");
}
function Dn(t) {
  var e = t.keyCode || t.which || t.charCode, r = y.indexOf(e);
  if (r >= 0 && y.splice(r, 1), t.key && t.key.toLowerCase() === "meta" && y.splice(0, y.length), (e === 93 || e === 224) && (e = 91), e in E) {
    E[e] = !1;
    for (var n in K)
      K[n] === e && (b[n] = !1);
  }
}
function Bn(t) {
  if (!t)
    Object.keys(T).forEach(function(s) {
      return delete T[s];
    });
  else if (Array.isArray(t))
    t.forEach(function(s) {
      s.key && Ze(s);
    });
  else if (typeof t == "object")
    t.key && Ze(t);
  else if (typeof t == "string") {
    for (var e = arguments.length, r = new Array(e > 1 ? e - 1 : 0), n = 1; n < e; n++)
      r[n - 1] = arguments[n];
    var i = r[0], a = r[1];
    typeof i == "function" && (a = i, i = ""), Ze({
      key: t,
      scope: i,
      method: a,
      splitKey: "+"
    });
  }
}
var Ze = function(e) {
  var r = e.key, n = e.scope, i = e.method, a = e.splitKey, s = a === void 0 ? "+" : a, o = Dr(r);
  o.forEach(function(c) {
    var l = c.split(s), u = l.length, h = l[u - 1], d = h === "*" ? "*" : Me(h);
    if (!!T[d]) {
      n || (n = fe());
      var p = u > 1 ? Nr(K, l) : [];
      T[d] = T[d].map(function(f) {
        var v = i ? f.method === i : !0;
        return v && f.scope === n && Hn(f.mods, p) ? {} : f;
      });
    }
  });
};
function Kt(t, e, r) {
  var n;
  if (e.scope === r || e.scope === "all") {
    n = e.mods.length > 0;
    for (var i in E)
      Object.prototype.hasOwnProperty.call(E, i) && (!E[i] && e.mods.indexOf(+i) > -1 || E[i] && e.mods.indexOf(+i) === -1) && (n = !1);
    (e.mods.length === 0 && !E[16] && !E[18] && !E[17] && !E[91] || n || e.shortcut === "*") && e.method(t, e) === !1 && (t.preventDefault ? t.preventDefault() : t.returnValue = !1, t.stopPropagation && t.stopPropagation(), t.cancelBubble && (t.cancelBubble = !0));
  }
}
function Ft(t) {
  var e = T["*"], r = t.keyCode || t.which || t.charCode;
  if (!!b.filter.call(this, t)) {
    if ((r === 93 || r === 224) && (r = 91), y.indexOf(r) === -1 && r !== 229 && y.push(r), ["ctrlKey", "altKey", "shiftKey", "metaKey"].forEach(function(p) {
      var f = zt[p];
      t[p] && y.indexOf(f) === -1 ? y.push(f) : !t[p] && y.indexOf(f) > -1 ? y.splice(y.indexOf(f), 1) : p === "metaKey" && t[p] && y.length === 3 && (t.ctrlKey || t.shiftKey || t.altKey || (y = y.slice(y.indexOf(f))));
    }), r in E) {
      E[r] = !0;
      for (var n in K)
        K[n] === r && (b[n] = !0);
      if (!e)
        return;
    }
    for (var i in E)
      Object.prototype.hasOwnProperty.call(E, i) && (E[i] = t[zt[i]]);
    t.getModifierState && !(t.altKey && !t.ctrlKey) && t.getModifierState("AltGraph") && (y.indexOf(17) === -1 && y.push(17), y.indexOf(18) === -1 && y.push(18), E[17] = !0, E[18] = !0);
    var a = fe();
    if (e)
      for (var s = 0; s < e.length; s++)
        e[s].scope === a && (t.type === "keydown" && e[s].keydown || t.type === "keyup" && e[s].keyup) && Kt(t, e[s], a);
    if (r in T) {
      for (var o = 0; o < T[r].length; o++)
        if ((t.type === "keydown" && T[r][o].keydown || t.type === "keyup" && T[r][o].keyup) && T[r][o].key) {
          for (var c = T[r][o], l = c.splitKey, u = c.key.split(l), h = [], d = 0; d < u.length; d++)
            h.push(Me(u[d]));
          h.sort().join("") === y.sort().join("") && Kt(t, c, a);
        }
    }
  }
}
function zn(t) {
  return Kr.indexOf(t) > -1;
}
function b(t, e, r) {
  y = [];
  var n = Dr(t), i = [], a = "all", s = document, o = 0, c = !1, l = !0, u = "+";
  for (r === void 0 && typeof e == "function" && (r = e), Object.prototype.toString.call(e) === "[object Object]" && (e.scope && (a = e.scope), e.element && (s = e.element), e.keyup && (c = e.keyup), e.keydown !== void 0 && (l = e.keydown), typeof e.splitKey == "string" && (u = e.splitKey)), typeof e == "string" && (a = e); o < n.length; o++)
    t = n[o].split(u), i = [], t.length > 1 && (i = Nr(K, t)), t = t[t.length - 1], t = t === "*" ? "*" : Me(t), t in T || (T[t] = []), T[t].push({
      keyup: c,
      keydown: l,
      scope: a,
      mods: i,
      shortcut: n[o],
      method: r,
      key: n[o],
      splitKey: u
    });
  typeof s < "u" && !zn(s) && window && (Kr.push(s), Xe(s, "keydown", function(h) {
    Ft(h);
  }), Xe(window, "focus", function() {
    y = [];
  }), Xe(s, "keyup", function(h) {
    Ft(h), Dn(h);
  }));
}
var Qe = {
  setScope: Fr,
  getScope: fe,
  deleteScope: Nn,
  getPressedKeyCodes: Rn,
  isPressed: Un,
  filter: Ln,
  unbind: Bn
};
for (var et in Qe)
  Object.prototype.hasOwnProperty.call(Qe, et) && (b[et] = Qe[et]);
if (typeof window < "u") {
  var Kn = window.hotkeys;
  b.noConflict = function(t) {
    return t && window.hotkeys === b && (window.hotkeys = Kn), b;
  }, window.hotkeys = b;
}
var ye = globalThis && globalThis.__decorate || function(t, e, r, n) {
  var i = arguments.length, a = i < 3 ? e : n === null ? n = Object.getOwnPropertyDescriptor(e, r) : n, s;
  if (typeof Reflect == "object" && typeof Reflect.decorate == "function")
    a = Reflect.decorate(t, e, r, n);
  else
    for (var o = t.length - 1; o >= 0; o--)
      (s = t[o]) && (a = (i < 3 ? s(a) : i > 3 ? s(e, r, a) : s(e, r)) || a);
  return i > 3 && a && Object.defineProperty(e, r, a), a;
};
let F = class extends L {
  constructor() {
    super(...arguments), this.placeholder = "", this.hideBreadcrumbs = !1, this.breadcrumbHome = "Home", this.breadcrumbs = [], this._inputRef = Lr();
  }
  render() {
    let e = "";
    if (!this.hideBreadcrumbs) {
      const r = [];
      for (const n of this.breadcrumbs)
        r.push(S`<button
            tabindex="-1"
            @click=${() => this.selectParent(n)}
            class="breadcrumb"
          >
            ${n}
          </button>`);
      e = S`<div class="breadcrumb-list">
        <button
          tabindex="-1"
          @click=${() => this.selectParent()}
          class="breadcrumb"
        >
          ${this.breadcrumbHome}
        </button>
        ${r}
      </div>`;
    }
    return S`
      ${e}
      <div part="ninja-input-wrapper" class="search-wrapper">
        <input
          part="ninja-input"
          type="text"
          id="search"
          spellcheck="false"
          autocomplete="off"
          @input="${this._handleInput}"
          ${Ur(this._inputRef)}
          placeholder="${this.placeholder}"
          class="search"
        />
      </div>
    `;
  }
  setSearch(e) {
    this._inputRef.value && (this._inputRef.value.value = e);
  }
  focusSearch() {
    requestAnimationFrame(() => this._inputRef.value.focus());
  }
  _handleInput(e) {
    const r = e.target;
    this.dispatchEvent(new CustomEvent("change", {
      detail: { search: r.value },
      bubbles: !1,
      composed: !1
    }));
  }
  selectParent(e) {
    this.dispatchEvent(new CustomEvent("setParent", {
      detail: { parent: e },
      bubbles: !0,
      composed: !0
    }));
  }
  firstUpdated() {
    this.focusSearch();
  }
  _close() {
    this.dispatchEvent(new CustomEvent("close", { bubbles: !0, composed: !0 }));
  }
};
F.styles = Pe`
    :host {
      flex: 1;
      position: relative;
    }
    .search {
      padding: 1.25em;
      flex-grow: 1;
      flex-shrink: 0;
      margin: 0px;
      border: none;
      appearance: none;
      font-size: 1.125em;
      background: transparent;
      caret-color: var(--ninja-accent-color);
      color: var(--ninja-text-color);
      outline: none;
      font-family: var(--ninja-font-family);
    }
    .search::placeholder {
      color: var(--ninja-placeholder-color);
    }
    .breadcrumb-list {
      padding: 1em 4em 0 1em;
      display: flex;
      flex-direction: row;
      align-items: stretch;
      justify-content: flex-start;
      flex: initial;
    }

    .breadcrumb {
      background: var(--ninja-secondary-background-color);
      text-align: center;
      line-height: 1.2em;
      border-radius: var(--ninja-key-border-radius);
      border: 0;
      cursor: pointer;
      padding: 0.1em 0.5em;
      color: var(--ninja-secondary-text-color);
      margin-right: 0.5em;
      outline: none;
      font-family: var(--ninja-font-family);
    }

    .search-wrapper {
      display: flex;
      border-bottom: var(--ninja-separate-border);
    }
  `;
ye([
  w()
], F.prototype, "placeholder", void 0);
ye([
  w({ type: Boolean })
], F.prototype, "hideBreadcrumbs", void 0);
ye([
  w()
], F.prototype, "breadcrumbHome", void 0);
ye([
  w({ type: Array })
], F.prototype, "breadcrumbs", void 0);
F = ye([
  ke("ninja-header")
], F);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class ot extends ge {
  constructor(e) {
    if (super(e), this.it = _, e.type !== P.CHILD)
      throw Error(this.constructor.directiveName + "() can only be used in child bindings");
  }
  render(e) {
    if (e === _ || e == null)
      return this._t = void 0, this.it = e;
    if (e === O)
      return e;
    if (typeof e != "string")
      throw Error(this.constructor.directiveName + "() called with a non-string value");
    if (e === this.it)
      return this._t;
    this.it = e;
    const r = [e];
    return r.raw = r, this._t = { _$litType$: this.constructor.resultType, strings: r, values: [] };
  }
}
ot.directiveName = "unsafeHTML", ot.resultType = 1;
const Fn = ve(ot);
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function* Gn(t, e) {
  const r = typeof e == "function";
  if (t !== void 0) {
    let n = -1;
    for (const i of t)
      n > -1 && (yield r ? e(n) : e), n++, yield i;
  }
}
function Vn(t, e, r, n) {
  var i = arguments.length, a = i < 3 ? e : n === null ? n = Object.getOwnPropertyDescriptor(e, r) : n, s;
  if (typeof Reflect == "object" && typeof Reflect.decorate == "function")
    a = Reflect.decorate(t, e, r, n);
  else
    for (var o = t.length - 1; o >= 0; o--)
      (s = t[o]) && (a = (i < 3 ? s(a) : i > 3 ? s(e, r, a) : s(e, r)) || a);
  return i > 3 && a && Object.defineProperty(e, r, a), a;
}
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-LIcense-Identifier: Apache-2.0
 */
const qn = Pe`:host{font-family:var(--mdc-icon-font, "Material Icons");font-weight:normal;font-style:normal;font-size:var(--mdc-icon-size, 24px);line-height:1;letter-spacing:normal;text-transform:none;display:inline-block;white-space:nowrap;word-wrap:normal;direction:ltr;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;-moz-osx-font-smoothing:grayscale;font-feature-settings:"liga"}`;
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
let lt = class extends L {
  render() {
    return S`<span><slot></slot></span>`;
  }
};
lt.styles = [qn];
lt = Vn([
  ke("mwc-icon")
], lt);
var Ie = globalThis && globalThis.__decorate || function(t, e, r, n) {
  var i = arguments.length, a = i < 3 ? e : n === null ? n = Object.getOwnPropertyDescriptor(e, r) : n, s;
  if (typeof Reflect == "object" && typeof Reflect.decorate == "function")
    a = Reflect.decorate(t, e, r, n);
  else
    for (var o = t.length - 1; o >= 0; o--)
      (s = t[o]) && (a = (i < 3 ? s(a) : i > 3 ? s(e, r, a) : s(e, r)) || a);
  return i > 3 && a && Object.defineProperty(e, r, a), a;
};
let J = class extends L {
  constructor() {
    super(), this.selected = !1, this.hotKeysJoinedView = !0, this.addEventListener("click", this.click);
  }
  ensureInView() {
    requestAnimationFrame(() => this.scrollIntoView({ block: "nearest" }));
  }
  click() {
    this.dispatchEvent(new CustomEvent("actionsSelected", {
      detail: this.action,
      bubbles: !0,
      composed: !0
    }));
  }
  updated(e) {
    e.has("selected") && this.selected && this.ensureInView();
  }
  render() {
    let e;
    this.action.mdIcon ? e = S`<mwc-icon part="ninja-icon" class="ninja-icon"
        >${this.action.mdIcon}</mwc-icon
      >` : this.action.icon && (e = Fn(this.action.icon || ""));
    let r;
    this.action.hotkey && (this.hotKeysJoinedView ? r = this.action.hotkey.split(",").map((i) => {
      const a = i.split("+"), s = S`${Gn(a.map((o) => S`<kbd>${o}</kbd>`), "+")}`;
      return S`<div class="ninja-hotkey ninja-hotkeys">
            ${s}
          </div>`;
    }) : r = this.action.hotkey.split(",").map((i) => {
      const s = i.split("+").map((o) => S`<kbd class="ninja-hotkey">${o}</kbd>`);
      return S`<kbd class="ninja-hotkeys">${s}</kbd>`;
    }));
    const n = {
      selected: this.selected,
      "ninja-action": !0
    };
    return S`
      <div
        class="ninja-action"
        part="ninja-action ${this.selected ? "ninja-selected" : ""}"
        class=${st(n)}
      >
        ${e}
        <div class="ninja-title">${this.action.title}</div>
        ${r}
      </div>
    `;
  }
};
J.styles = Pe`
    :host {
      display: flex;
      width: 100%;
    }
    .ninja-action {
      padding: 0.75em 1em;
      display: flex;
      border-left: 2px solid transparent;
      align-items: center;
      justify-content: start;
      outline: none;
      transition: color 0s ease 0s;
      width: 100%;
    }
    .ninja-action.selected {
      cursor: pointer;
      color: var(--ninja-selected-text-color);
      background-color: var(--ninja-selected-background);
      border-left: 2px solid var(--ninja-accent-color);
      outline: none;
    }
    .ninja-action.selected .ninja-icon {
      color: var(--ninja-selected-text-color);
    }
    .ninja-icon {
      font-size: var(--ninja-icon-size);
      max-width: var(--ninja-icon-size);
      max-height: var(--ninja-icon-size);
      margin-right: 1em;
      color: var(--ninja-icon-color);
      margin-right: 1em;
      position: relative;
    }

    .ninja-title {
      flex-shrink: 0.01;
      margin-right: 0.5em;
      flex-grow: 1;
      font-size: 0.8125em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .ninja-hotkeys {
      flex-shrink: 0;
      width: min-content;
      display: flex;
    }

    .ninja-hotkeys kbd {
      font-family: inherit;
    }
    .ninja-hotkey {
      background: var(--ninja-secondary-background-color);
      padding: 0.06em 0.25em;
      border-radius: var(--ninja-key-border-radius);
      text-transform: capitalize;
      color: var(--ninja-secondary-text-color);
      font-size: 0.75em;
      font-family: inherit;
    }

    .ninja-hotkey + .ninja-hotkey {
      margin-left: 0.5em;
    }
    .ninja-hotkeys + .ninja-hotkeys {
      margin-left: 1em;
    }
  `;
Ie([
  w({ type: Object })
], J.prototype, "action", void 0);
Ie([
  w({ type: Boolean })
], J.prototype, "selected", void 0);
Ie([
  w({ type: Boolean })
], J.prototype, "hotKeysJoinedView", void 0);
J = Ie([
  ke("ninja-action")
], J);
const Wn = S` <div class="modal-footer" slot="footer">
  <span class="help">
    <svg
      version="1.0"
      class="ninja-examplekey"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1280 1280"
    >
      <path
        d="M1013 376c0 73.4-.4 113.3-1.1 120.2a159.9 159.9 0 0 1-90.2 127.3c-20 9.6-36.7 14-59.2 15.5-7.1.5-121.9.9-255 1h-242l95.5-95.5 95.5-95.5-38.3-38.2-38.2-38.3-160 160c-88 88-160 160.4-160 161 0 .6 72 73 160 161l160 160 38.2-38.3 38.3-38.2-95.5-95.5-95.5-95.5h251.1c252.9 0 259.8-.1 281.4-3.6 72.1-11.8 136.9-54.1 178.5-116.4 8.6-12.9 22.6-40.5 28-55.4 4.4-12 10.7-36.1 13.1-50.6 1.6-9.6 1.8-21 2.1-132.8l.4-122.2H1013v110z"
      />
    </svg>

    to select
  </span>
  <span class="help">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      class="ninja-examplekey"
      viewBox="0 0 24 24"
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path
        d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"
      />
    </svg>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      class="ninja-examplekey"
      viewBox="0 0 24 24"
    >
      <path d="M0 0h24v24H0V0z" fill="none" />
      <path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z" />
    </svg>
    to navigate
  </span>
  <span class="help">
    <span class="ninja-examplekey esc">esc</span>
    to close
  </span>
  <span class="help">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      class="ninja-examplekey backspace"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fill-rule="evenodd"
        d="M6.707 4.879A3 3 0 018.828 4H15a3 3 0 013 3v6a3 3 0 01-3 3H8.828a3 3 0 01-2.12-.879l-4.415-4.414a1 1 0 010-1.414l4.414-4.414zm4 2.414a1 1 0 00-1.414 1.414L10.586 10l-1.293 1.293a1 1 0 101.414 1.414L12 11.414l1.293 1.293a1 1 0 001.414-1.414L13.414 10l1.293-1.293a1 1 0 00-1.414-1.414L12 8.586l-1.293-1.293z"
        clip-rule="evenodd"
      />
    </svg>
    move to parent
  </span>
</div>`, Yn = Pe`
  :host {
    --ninja-width: 640px;
    --ninja-backdrop-filter: none;
    --ninja-overflow-background: rgba(255, 255, 255, 0.5);
    --ninja-text-color: rgb(60, 65, 73);
    --ninja-font-size: 16px;
    --ninja-top: 20%;

    --ninja-key-border-radius: 0.25em;
    --ninja-accent-color: rgb(110, 94, 210);
    --ninja-secondary-background-color: rgb(239, 241, 244);
    --ninja-secondary-text-color: rgb(107, 111, 118);

    --ninja-selected-background: rgb(248, 249, 251);

    --ninja-icon-color: var(--ninja-secondary-text-color);
    --ninja-icon-size: 1.2em;
    --ninja-separate-border: 1px solid var(--ninja-secondary-background-color);

    --ninja-modal-background: #fff;
    --ninja-modal-shadow: rgb(0 0 0 / 50%) 0px 16px 70px;

    --ninja-actions-height: 300px;
    --ninja-group-text-color: rgb(144, 149, 157);

    --ninja-footer-background: rgba(242, 242, 242, 0.4);

    --ninja-placeholder-color: #8e8e8e;

    font-size: var(--ninja-font-size);

    --ninja-z-index: 1;
  }

  :host(.dark) {
    --ninja-backdrop-filter: none;
    --ninja-overflow-background: rgba(0, 0, 0, 0.7);
    --ninja-text-color: #7d7d7d;

    --ninja-modal-background: rgba(17, 17, 17, 0.85);
    --ninja-accent-color: rgb(110, 94, 210);
    --ninja-secondary-background-color: rgba(51, 51, 51, 0.44);
    --ninja-secondary-text-color: #888;

    --ninja-selected-text-color: #eaeaea;
    --ninja-selected-background: rgba(51, 51, 51, 0.44);

    --ninja-icon-color: var(--ninja-secondary-text-color);
    --ninja-separate-border: 1px solid var(--ninja-secondary-background-color);

    --ninja-modal-shadow: 0 16px 70px rgba(0, 0, 0, 0.2);

    --ninja-group-text-color: rgb(144, 149, 157);

    --ninja-footer-background: rgba(30, 30, 30, 85%);
  }

  .modal {
    display: none;
    position: fixed;
    z-index: var(--ninja-z-index);
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: auto;
    background: var(--ninja-overflow-background);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-backdrop-filter: var(--ninja-backdrop-filter);
    backdrop-filter: var(--ninja-backdrop-filter);
    text-align: left;
    color: var(--ninja-text-color);
    font-family: var(--ninja-font-family);
  }
  .modal.visible {
    display: block;
  }

  .modal-content {
    position: relative;
    top: var(--ninja-top);
    margin: auto;
    padding: 0;
    display: flex;
    flex-direction: column;
    flex-shrink: 1;
    -webkit-box-flex: 1;
    flex-grow: 1;
    min-width: 0px;
    will-change: transform;
    background: var(--ninja-modal-background);
    border-radius: 0.5em;
    box-shadow: var(--ninja-modal-shadow);
    max-width: var(--ninja-width);
    overflow: hidden;
  }

  .bump {
    animation: zoom-in-zoom-out 0.2s ease;
  }

  @keyframes zoom-in-zoom-out {
    0% {
      transform: scale(0.99);
    }
    50% {
      transform: scale(1.01, 1.01);
    }
    100% {
      transform: scale(1, 1);
    }
  }

  .ninja-github {
    color: var(--ninja-keys-text-color);
    font-weight: normal;
    text-decoration: none;
  }

  .actions-list {
    max-height: var(--ninja-actions-height);
    overflow: auto;
    scroll-behavior: smooth;
    position: relative;
    margin: 0;
    padding: 0.5em 0;
    list-style: none;
    scroll-behavior: smooth;
  }

  .group-header {
    height: 1.375em;
    line-height: 1.375em;
    padding-left: 1.25em;
    padding-top: 0.5em;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    font-size: 0.75em;
    line-height: 1em;
    color: var(--ninja-group-text-color);
    margin: 1px 0;
  }

  .modal-footer {
    background: var(--ninja-footer-background);
    padding: 0.5em 1em;
    display: flex;
    /* font-size: 0.75em; */
    border-top: var(--ninja-separate-border);
    color: var(--ninja-secondary-text-color);
  }

  .modal-footer .help {
    display: flex;
    margin-right: 1em;
    align-items: center;
    font-size: 0.75em;
  }

  .ninja-examplekey {
    background: var(--ninja-secondary-background-color);
    padding: 0.06em 0.25em;
    border-radius: var(--ninja-key-border-radius);
    color: var(--ninja-secondary-text-color);
    width: 1em;
    height: 1em;
    margin-right: 0.5em;
    font-size: 1.25em;
    fill: currentColor;
  }
  .ninja-examplekey.esc {
    width: auto;
    height: auto;
    font-size: 1.1em;
  }
  .ninja-examplekey.backspace {
    opacity: 0.7;
  }
`;
var A = globalThis && globalThis.__decorate || function(t, e, r, n) {
  var i = arguments.length, a = i < 3 ? e : n === null ? n = Object.getOwnPropertyDescriptor(e, r) : n, s;
  if (typeof Reflect == "object" && typeof Reflect.decorate == "function")
    a = Reflect.decorate(t, e, r, n);
  else
    for (var o = t.length - 1; o >= 0; o--)
      (s = t[o]) && (a = (i < 3 ? s(a) : i > 3 ? s(e, r, a) : s(e, r)) || a);
  return i > 3 && a && Object.defineProperty(e, r, a), a;
};
let $ = class extends L {
  constructor() {
    super(...arguments), this.placeholder = "Type a command or search...", this.disableHotkeys = !1, this.hideBreadcrumbs = !1, this.openHotkey = "cmd+k,ctrl+k", this.navigationUpHotkey = "up,shift+tab", this.navigationDownHotkey = "down,tab", this.closeHotkey = "esc", this.goBackHotkey = "backspace", this.selectHotkey = "enter", this.hotKeysJoinedView = !1, this.noAutoLoadMdIcons = !1, this.data = [], this.visible = !1, this._bump = !0, this._actionMatches = [], this._search = "", this._flatData = [], this._headerRef = Lr();
  }
  open(e = {}) {
    this._bump = !0, this.visible = !0, this._headerRef.value.focusSearch(), this._actionMatches.length > 0 && (this._selected = this._actionMatches[0]), this.setParent(e.parent);
  }
  close() {
    this._bump = !1, this.visible = !1;
  }
  setParent(e) {
    e ? this._currentRoot = e : this._currentRoot = void 0, this._selected = void 0, this._search = "", this._headerRef.value.setSearch("");
  }
  get breadcrumbs() {
    var e;
    const r = [];
    let n = (e = this._selected) === null || e === void 0 ? void 0 : e.parent;
    if (n)
      for (r.push(n); n; ) {
        const i = this._flatData.find((a) => a.id === n);
        i != null && i.parent && r.push(i.parent), n = i ? i.parent : void 0;
      }
    return r.reverse();
  }
  connectedCallback() {
    super.connectedCallback(), this.noAutoLoadMdIcons || document.fonts.load("24px Material Icons", "apps").then(() => {
    }), this._registerInternalHotkeys();
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._unregisterInternalHotkeys();
  }
  _flattern(e, r) {
    let n = [];
    return e || (e = []), e.map((i) => {
      const a = i.children && i.children.some((o) => typeof o == "string"), s = { ...i, parent: i.parent || r };
      return a || (s.children && s.children.length && (r = i.id, n = [...n, ...s.children]), s.children = s.children ? s.children.map((o) => o.id) : []), s;
    }).concat(n.length ? this._flattern(n, r) : n);
  }
  update(e) {
    e.has("data") && !this.disableHotkeys && (this._flatData = this._flattern(this.data), this._flatData.filter((r) => !!r.hotkey).forEach((r) => {
      b(r.hotkey, (n) => {
        n.preventDefault(), r.handler && r.handler(r);
      });
    })), super.update(e);
  }
  _registerInternalHotkeys() {
    this.openHotkey && b(this.openHotkey, (e) => {
      e.preventDefault(), this.visible ? this.close() : this.open();
    }), this.selectHotkey && b(this.selectHotkey, (e) => {
      !this.visible || (e.preventDefault(), this._actionSelected(this._actionMatches[this._selectedIndex]));
    }), this.goBackHotkey && b(this.goBackHotkey, (e) => {
      !this.visible || this._search || (e.preventDefault(), this._goBack());
    }), this.navigationDownHotkey && b(this.navigationDownHotkey, (e) => {
      !this.visible || (e.preventDefault(), this._selectedIndex >= this._actionMatches.length - 1 ? this._selected = this._actionMatches[0] : this._selected = this._actionMatches[this._selectedIndex + 1]);
    }), this.navigationUpHotkey && b(this.navigationUpHotkey, (e) => {
      !this.visible || (e.preventDefault(), this._selectedIndex === 0 ? this._selected = this._actionMatches[this._actionMatches.length - 1] : this._selected = this._actionMatches[this._selectedIndex - 1]);
    }), this.closeHotkey && b(this.closeHotkey, () => {
      !this.visible || this.close();
    });
  }
  _unregisterInternalHotkeys() {
    this.openHotkey && b.unbind(this.openHotkey), this.selectHotkey && b.unbind(this.selectHotkey), this.goBackHotkey && b.unbind(this.goBackHotkey), this.navigationDownHotkey && b.unbind(this.navigationDownHotkey), this.navigationUpHotkey && b.unbind(this.navigationUpHotkey), this.closeHotkey && b.unbind(this.closeHotkey);
  }
  _actionFocused(e, r) {
    this._selected = e, r.target.ensureInView();
  }
  _onTransitionEnd() {
    this._bump = !1;
  }
  _goBack() {
    const e = this.breadcrumbs.length > 1 ? this.breadcrumbs[this.breadcrumbs.length - 2] : void 0;
    this.setParent(e);
  }
  render() {
    const e = {
      bump: this._bump,
      "modal-content": !0
    }, r = {
      visible: this.visible,
      modal: !0
    }, i = this._flatData.filter((o) => {
      var c;
      const l = new RegExp(this._search, "gi"), u = o.title.match(l) || ((c = o.keywords) === null || c === void 0 ? void 0 : c.match(l));
      return (!this._currentRoot && this._search || o.parent === this._currentRoot) && u;
    }).reduce((o, c) => o.set(c.section, [...o.get(c.section) || [], c]), /* @__PURE__ */ new Map());
    this._actionMatches = [...i.values()].flat(), this._actionMatches.length > 0 && this._selectedIndex === -1 && (this._selected = this._actionMatches[0]), this._actionMatches.length === 0 && (this._selected = void 0);
    const a = (o) => S` ${jn(o, (c) => c.id, (c) => {
      var l;
      return S`<ninja-action
            exportparts="ninja-action,ninja-selected,ninja-icon"
            .selected=${Cn(c.id === ((l = this._selected) === null || l === void 0 ? void 0 : l.id))}
            .hotKeysJoinedView=${this.hotKeysJoinedView}
            @mouseover=${(u) => this._actionFocused(c, u)}
            @actionsSelected=${(u) => this._actionSelected(u.detail)}
            .action=${c}
          ></ninja-action>`;
    })}`, s = [];
    return i.forEach((o, c) => {
      const l = c ? S`<div class="group-header">${c}</div>` : void 0;
      s.push(S`${l}${a(o)}`);
    }), S`
      <div @click=${this._overlayClick} class=${st(r)}>
        <div class=${st(e)} @animationend=${this._onTransitionEnd}>
          <ninja-header
            exportparts="ninja-input,ninja-input-wrapper"
            ${Ur(this._headerRef)}
            .placeholder=${this.placeholder}
            .hideBreadcrumbs=${this.hideBreadcrumbs}
            .breadcrumbs=${this.breadcrumbs}
            @change=${this._handleInput}
            @setParent=${(o) => this.setParent(o.detail.parent)}
            @close=${this.close}
          >
          </ninja-header>
          <div class="modal-body">
            <div class="actions-list" part="actions-list">${s}</div>
          </div>
          <slot name="footer"> ${Wn} </slot>
        </div>
      </div>
    `;
  }
  get _selectedIndex() {
    return this._selected ? this._actionMatches.indexOf(this._selected) : -1;
  }
  _actionSelected(e) {
    var r;
    if (this.dispatchEvent(new CustomEvent("selected", {
      detail: { search: this._search, action: e },
      bubbles: !0,
      composed: !0
    })), !!e) {
      if (e.children && ((r = e.children) === null || r === void 0 ? void 0 : r.length) > 0 && (this._currentRoot = e.id, this._search = ""), this._headerRef.value.setSearch(""), this._headerRef.value.focusSearch(), e.handler) {
        const n = e.handler(e);
        n != null && n.keepOpen || this.close();
      }
      this._bump = !0;
    }
  }
  async _handleInput(e) {
    this._search = e.detail.search, await this.updateComplete, this.dispatchEvent(new CustomEvent("change", {
      detail: { search: this._search, actions: this._actionMatches },
      bubbles: !0,
      composed: !0
    }));
  }
  _overlayClick(e) {
    var r;
    !((r = e.target) === null || r === void 0) && r.classList.contains("modal") && this.close();
  }
};
$.styles = [Yn];
A([
  w({ type: String })
], $.prototype, "placeholder", void 0);
A([
  w({ type: Boolean })
], $.prototype, "disableHotkeys", void 0);
A([
  w({ type: Boolean })
], $.prototype, "hideBreadcrumbs", void 0);
A([
  w()
], $.prototype, "openHotkey", void 0);
A([
  w()
], $.prototype, "navigationUpHotkey", void 0);
A([
  w()
], $.prototype, "navigationDownHotkey", void 0);
A([
  w()
], $.prototype, "closeHotkey", void 0);
A([
  w()
], $.prototype, "goBackHotkey", void 0);
A([
  w()
], $.prototype, "selectHotkey", void 0);
A([
  w({ type: Boolean })
], $.prototype, "hotKeysJoinedView", void 0);
A([
  w({ type: Boolean })
], $.prototype, "noAutoLoadMdIcons", void 0);
A([
  w({
    type: Array,
    hasChanged() {
      return !0;
    }
  })
], $.prototype, "data", void 0);
A([
  U()
], $.prototype, "visible", void 0);
A([
  U()
], $.prototype, "_bump", void 0);
A([
  U()
], $.prototype, "_actionMatches", void 0);
A([
  U()
], $.prototype, "_search", void 0);
A([
  U()
], $.prototype, "_currentRoot", void 0);
A([
  U()
], $.prototype, "_flatData", void 0);
A([
  U()
], $.prototype, "breadcrumbs", null);
A([
  U()
], $.prototype, "_selected", void 0);
$ = A([
  ke("ninja-keys")
], $);
const ct = {};
function Jn(t) {
  return Object.keys(ct).reduce(
    (e, r) => e.split(`:${r}`).join(ct[r]),
    t
  );
}
function Xn(t) {
  Object.assign(ct, t);
}
function Zn(t) {
  return {
    section: t.section,
    id: t.id,
    title: t.title,
    handler() {
      window.location.href = Jn(t.url);
    }
  };
}
const Qn = [
  {
    section: "navigation",
    id: "Tasks",
    title: "Tasks",
    url: ":adminUrl/schedule/tasks/mine"
  },
  {
    section: "navigation",
    id: "Docs",
    title: "Docs",
    url: ":onelifeUrl/admin/inbox"
  },
  {
    section: "navigation",
    id: "Faxes",
    title: "Faxes",
    url: ":onelifeUrl/fax/send_fax"
  },
  {
    section: "navigation",
    id: "Contacts",
    title: "Contacts",
    url: ":adminUrl/admin/contacts"
  },
  {
    section: "navigation",
    id: "Providers",
    title: "Providers",
    url: ":adminUrl/admin/provider-directory"
  },
  {
    section: "navigation",
    id: "Panel Management",
    title: "Panel Management",
    url: "/panel-management"
  },
  {
    section: "navigation",
    id: "Appointments Scanner",
    title: "Appointments Scanner",
    url: ":adminUrl/schedule/appointments-scanner"
  },
  {
    section: "navigation",
    id: "Appointments Cancellations",
    title: "Appointments Cancellations",
    url: ":adminUrl/schedule/appointment-cancellation-requests"
  },
  {
    section: "navigation",
    id: "Provider Schedule",
    title: "Provider Schedule",
    url: ":adminUrl/schedule/provider-schedule"
  },
  {
    section: "navigation",
    id: "Provider Appointment Inventory",
    title: "Provider Appointment Inventory",
    url: ":adminUrl/schedule/provider-appt-inventory"
  },
  {
    section: "navigation",
    id: "Phlebotomy Visits",
    title: "Phlebotomy Visits",
    url: ":adminUrl/schedule/phlebotomy-visits"
  },
  {
    section: "navigation",
    id: "Unregistered Patients",
    title: "Unregistered Patients",
    url: ":onelifeUrl/admin/appointments/unregistered"
  },
  {
    section: "navigation",
    id: "Virtual Visits",
    title: "Virtual Visits",
    url: ":onelifeUrl/admin/video_appointments"
  }
], ce = {};
function Gr(...t) {
  for (const e of t)
    ce[e.id] = e.url;
}
function ei(...t) {
  for (const e of t)
    delete ce[e];
}
function ti(t) {
  return new Promise((e, r) => {
    const n = document.createElement("link");
    n.rel = "prerender", n.href = t, n.as = "document", n.crossOrigin = "use-credentials", n.onload = e, n.onerror = (...i) => {
      console.error(t, "error args", ...i), r(new Error("failed to preload"));
    }, document.head.appendChild(n);
  });
}
async function ri(t) {
  ce[t] && (await ti(ce[t]), delete ce[t]);
}
function ni(t) {
  t.addEventListener(
    "change",
    async ({ detail: e }) => {
      const { actions: r } = e;
      r.length < 3 && await Promise.all(r.map((n) => n.id).map(ri)).catch(
        () => {
          console.warn("error preloading");
        }
      );
    }
  );
}
function ii(t) {
  t.data = [
    ...t.data,
    ...$t(Qn)
  ], ni(t);
}
function $t(t) {
  return Gr(...t), t.map(Zn);
}
var we = typeof globalThis < "u" ? globalThis : typeof window < "u" ? window : typeof global < "u" ? global : typeof self < "u" ? self : {};
function ai() {
}
var Vr = ai;
function si(t, e) {
  for (var r = -1, n = t == null ? 0 : t.length, i = 0, a = []; ++r < n; ) {
    var s = t[r];
    e(s, r, t) && (a[i++] = s);
  }
  return a;
}
var qr = si;
function oi(t) {
  return function(e, r, n) {
    for (var i = -1, a = Object(e), s = n(e), o = s.length; o--; ) {
      var c = s[t ? o : ++i];
      if (r(a[c], c, a) === !1)
        break;
    }
    return e;
  };
}
var li = oi, ci = li, di = ci(), hi = di;
function ui(t, e) {
  for (var r = -1, n = Array(t); ++r < t; )
    n[r] = e(r);
  return n;
}
var fi = ui, pi = typeof we == "object" && we && we.Object === Object && we, Wr = pi, vi = Wr, gi = typeof self == "object" && self && self.Object === Object && self, yi = vi || gi || Function("return this")(), k = yi, _i = k, $i = _i.Symbol, He = $i, Gt = He, Yr = Object.prototype, mi = Yr.hasOwnProperty, bi = Yr.toString, oe = Gt ? Gt.toStringTag : void 0;
function Ai(t) {
  var e = mi.call(t, oe), r = t[oe];
  try {
    t[oe] = void 0;
    var n = !0;
  } catch {
  }
  var i = bi.call(t);
  return n && (e ? t[oe] = r : delete t[oe]), i;
}
var wi = Ai, xi = Object.prototype, Ti = xi.toString;
function Ei(t) {
  return Ti.call(t);
}
var Si = Ei, Vt = He, ji = wi, Ci = Si, Oi = "[object Null]", Pi = "[object Undefined]", qt = Vt ? Vt.toStringTag : void 0;
function ki(t) {
  return t == null ? t === void 0 ? Pi : Oi : qt && qt in Object(t) ? ji(t) : Ci(t);
}
var _e = ki;
function Mi(t) {
  return t != null && typeof t == "object";
}
var $e = Mi, Ii = _e, Hi = $e, Ri = "[object Arguments]";
function Li(t) {
  return Hi(t) && Ii(t) == Ri;
}
var Ui = Li, Wt = Ui, Ni = $e, Jr = Object.prototype, Di = Jr.hasOwnProperty, Bi = Jr.propertyIsEnumerable, zi = Wt(function() {
  return arguments;
}()) ? Wt : function(t) {
  return Ni(t) && Di.call(t, "callee") && !Bi.call(t, "callee");
}, Xr = zi, Ki = Array.isArray, I = Ki, Ce = { exports: {} };
function Fi() {
  return !1;
}
var Gi = Fi;
(function(t, e) {
  var r = k, n = Gi, i = e && !e.nodeType && e, a = i && !0 && t && !t.nodeType && t, s = a && a.exports === i, o = s ? r.Buffer : void 0, c = o ? o.isBuffer : void 0, l = c || n;
  t.exports = l;
})(Ce, Ce.exports);
var Vi = 9007199254740991, qi = /^(?:0|[1-9]\d*)$/;
function Wi(t, e) {
  var r = typeof t;
  return e = e == null ? Vi : e, !!e && (r == "number" || r != "symbol" && qi.test(t)) && t > -1 && t % 1 == 0 && t < e;
}
var Zr = Wi, Yi = 9007199254740991;
function Ji(t) {
  return typeof t == "number" && t > -1 && t % 1 == 0 && t <= Yi;
}
var mt = Ji, Xi = _e, Zi = mt, Qi = $e, ea = "[object Arguments]", ta = "[object Array]", ra = "[object Boolean]", na = "[object Date]", ia = "[object Error]", aa = "[object Function]", sa = "[object Map]", oa = "[object Number]", la = "[object Object]", ca = "[object RegExp]", da = "[object Set]", ha = "[object String]", ua = "[object WeakMap]", fa = "[object ArrayBuffer]", pa = "[object DataView]", va = "[object Float32Array]", ga = "[object Float64Array]", ya = "[object Int8Array]", _a = "[object Int16Array]", $a = "[object Int32Array]", ma = "[object Uint8Array]", ba = "[object Uint8ClampedArray]", Aa = "[object Uint16Array]", wa = "[object Uint32Array]", g = {};
g[va] = g[ga] = g[ya] = g[_a] = g[$a] = g[ma] = g[ba] = g[Aa] = g[wa] = !0;
g[ea] = g[ta] = g[fa] = g[ra] = g[pa] = g[na] = g[ia] = g[aa] = g[sa] = g[oa] = g[la] = g[ca] = g[da] = g[ha] = g[ua] = !1;
function xa(t) {
  return Qi(t) && Zi(t.length) && !!g[Xi(t)];
}
var Ta = xa;
function Ea(t) {
  return function(e) {
    return t(e);
  };
}
var Sa = Ea, dt = { exports: {} };
(function(t, e) {
  var r = Wr, n = e && !e.nodeType && e, i = n && !0 && t && !t.nodeType && t, a = i && i.exports === n, s = a && r.process, o = function() {
    try {
      var c = i && i.require && i.require("util").types;
      return c || s && s.binding && s.binding("util");
    } catch {
    }
  }();
  t.exports = o;
})(dt, dt.exports);
var ja = Ta, Ca = Sa, Yt = dt.exports, Jt = Yt && Yt.isTypedArray, Oa = Jt ? Ca(Jt) : ja, Qr = Oa, Pa = fi, ka = Xr, Ma = I, Ia = Ce.exports, Ha = Zr, Ra = Qr, La = Object.prototype, Ua = La.hasOwnProperty;
function Na(t, e) {
  var r = Ma(t), n = !r && ka(t), i = !r && !n && Ia(t), a = !r && !n && !i && Ra(t), s = r || n || i || a, o = s ? Pa(t.length, String) : [], c = o.length;
  for (var l in t)
    (e || Ua.call(t, l)) && !(s && (l == "length" || i && (l == "offset" || l == "parent") || a && (l == "buffer" || l == "byteLength" || l == "byteOffset") || Ha(l, c))) && o.push(l);
  return o;
}
var Da = Na, Ba = Object.prototype;
function za(t) {
  var e = t && t.constructor, r = typeof e == "function" && e.prototype || Ba;
  return t === r;
}
var Ka = za;
function Fa(t, e) {
  return function(r) {
    return t(e(r));
  };
}
var Ga = Fa, Va = Ga, qa = Va(Object.keys, Object), Wa = qa, Ya = Ka, Ja = Wa, Xa = Object.prototype, Za = Xa.hasOwnProperty;
function Qa(t) {
  if (!Ya(t))
    return Ja(t);
  var e = [];
  for (var r in Object(t))
    Za.call(t, r) && r != "constructor" && e.push(r);
  return e;
}
var es = Qa;
function ts(t) {
  var e = typeof t;
  return t != null && (e == "object" || e == "function");
}
var me = ts, rs = _e, ns = me, is = "[object AsyncFunction]", as = "[object Function]", ss = "[object GeneratorFunction]", os = "[object Proxy]";
function ls(t) {
  if (!ns(t))
    return !1;
  var e = rs(t);
  return e == as || e == ss || e == is || e == os;
}
var en = ls, cs = en, ds = mt;
function hs(t) {
  return t != null && ds(t.length) && !cs(t);
}
var tn = hs, us = Da, fs = es, ps = tn;
function vs(t) {
  return ps(t) ? us(t) : fs(t);
}
var bt = vs, gs = hi, ys = bt;
function _s(t, e) {
  return t && gs(t, e, ys);
}
var $s = _s, ms = tn;
function bs(t, e) {
  return function(r, n) {
    if (r == null)
      return r;
    if (!ms(r))
      return t(r, n);
    for (var i = r.length, a = e ? i : -1, s = Object(r); (e ? a-- : ++a < i) && n(s[a], a, s) !== !1; )
      ;
    return r;
  };
}
var As = bs, ws = $s, xs = As, Ts = xs(ws), Es = Ts, Ss = Es;
function js(t, e) {
  var r = [];
  return Ss(t, function(n, i, a) {
    e(n, i, a) && r.push(n);
  }), r;
}
var Cs = js;
function Os() {
  this.__data__ = [], this.size = 0;
}
var Ps = Os;
function ks(t, e) {
  return t === e || t !== t && e !== e;
}
var rn = ks, Ms = rn;
function Is(t, e) {
  for (var r = t.length; r--; )
    if (Ms(t[r][0], e))
      return r;
  return -1;
}
var Re = Is, Hs = Re, Rs = Array.prototype, Ls = Rs.splice;
function Us(t) {
  var e = this.__data__, r = Hs(e, t);
  if (r < 0)
    return !1;
  var n = e.length - 1;
  return r == n ? e.pop() : Ls.call(e, r, 1), --this.size, !0;
}
var Ns = Us, Ds = Re;
function Bs(t) {
  var e = this.__data__, r = Ds(e, t);
  return r < 0 ? void 0 : e[r][1];
}
var zs = Bs, Ks = Re;
function Fs(t) {
  return Ks(this.__data__, t) > -1;
}
var Gs = Fs, Vs = Re;
function qs(t, e) {
  var r = this.__data__, n = Vs(r, t);
  return n < 0 ? (++this.size, r.push([t, e])) : r[n][1] = e, this;
}
var Ws = qs, Ys = Ps, Js = Ns, Xs = zs, Zs = Gs, Qs = Ws;
function Z(t) {
  var e = -1, r = t == null ? 0 : t.length;
  for (this.clear(); ++e < r; ) {
    var n = t[e];
    this.set(n[0], n[1]);
  }
}
Z.prototype.clear = Ys;
Z.prototype.delete = Js;
Z.prototype.get = Xs;
Z.prototype.has = Zs;
Z.prototype.set = Qs;
var Le = Z, eo = Le;
function to() {
  this.__data__ = new eo(), this.size = 0;
}
var ro = to;
function no(t) {
  var e = this.__data__, r = e.delete(t);
  return this.size = e.size, r;
}
var io = no;
function ao(t) {
  return this.__data__.get(t);
}
var so = ao;
function oo(t) {
  return this.__data__.has(t);
}
var lo = oo, co = k, ho = co["__core-js_shared__"], uo = ho, tt = uo, Xt = function() {
  var t = /[^.]+$/.exec(tt && tt.keys && tt.keys.IE_PROTO || "");
  return t ? "Symbol(src)_1." + t : "";
}();
function fo(t) {
  return !!Xt && Xt in t;
}
var po = fo, vo = Function.prototype, go = vo.toString;
function yo(t) {
  if (t != null) {
    try {
      return go.call(t);
    } catch {
    }
    try {
      return t + "";
    } catch {
    }
  }
  return "";
}
var nn = yo, _o = en, $o = po, mo = me, bo = nn, Ao = /[\\^$.*+?()[\]{}|]/g, wo = /^\[object .+?Constructor\]$/, xo = Function.prototype, To = Object.prototype, Eo = xo.toString, So = To.hasOwnProperty, jo = RegExp(
  "^" + Eo.call(So).replace(Ao, "\\$&").replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, "$1.*?") + "$"
);
function Co(t) {
  if (!mo(t) || $o(t))
    return !1;
  var e = _o(t) ? jo : wo;
  return e.test(bo(t));
}
var Oo = Co;
function Po(t, e) {
  return t == null ? void 0 : t[e];
}
var ko = Po, Mo = Oo, Io = ko;
function Ho(t, e) {
  var r = Io(t, e);
  return Mo(r) ? r : void 0;
}
var Q = Ho, Ro = Q, Lo = k, Uo = Ro(Lo, "Map"), At = Uo, No = Q, Do = No(Object, "create"), Ue = Do, Zt = Ue;
function Bo() {
  this.__data__ = Zt ? Zt(null) : {}, this.size = 0;
}
var zo = Bo;
function Ko(t) {
  var e = this.has(t) && delete this.__data__[t];
  return this.size -= e ? 1 : 0, e;
}
var Fo = Ko, Go = Ue, Vo = "__lodash_hash_undefined__", qo = Object.prototype, Wo = qo.hasOwnProperty;
function Yo(t) {
  var e = this.__data__;
  if (Go) {
    var r = e[t];
    return r === Vo ? void 0 : r;
  }
  return Wo.call(e, t) ? e[t] : void 0;
}
var Jo = Yo, Xo = Ue, Zo = Object.prototype, Qo = Zo.hasOwnProperty;
function el(t) {
  var e = this.__data__;
  return Xo ? e[t] !== void 0 : Qo.call(e, t);
}
var tl = el, rl = Ue, nl = "__lodash_hash_undefined__";
function il(t, e) {
  var r = this.__data__;
  return this.size += this.has(t) ? 0 : 1, r[t] = rl && e === void 0 ? nl : e, this;
}
var al = il, sl = zo, ol = Fo, ll = Jo, cl = tl, dl = al;
function ee(t) {
  var e = -1, r = t == null ? 0 : t.length;
  for (this.clear(); ++e < r; ) {
    var n = t[e];
    this.set(n[0], n[1]);
  }
}
ee.prototype.clear = sl;
ee.prototype.delete = ol;
ee.prototype.get = ll;
ee.prototype.has = cl;
ee.prototype.set = dl;
var hl = ee, Qt = hl, ul = Le, fl = At;
function pl() {
  this.size = 0, this.__data__ = {
    hash: new Qt(),
    map: new (fl || ul)(),
    string: new Qt()
  };
}
var vl = pl;
function gl(t) {
  var e = typeof t;
  return e == "string" || e == "number" || e == "symbol" || e == "boolean" ? t !== "__proto__" : t === null;
}
var yl = gl, _l = yl;
function $l(t, e) {
  var r = t.__data__;
  return _l(e) ? r[typeof e == "string" ? "string" : "hash"] : r.map;
}
var Ne = $l, ml = Ne;
function bl(t) {
  var e = ml(this, t).delete(t);
  return this.size -= e ? 1 : 0, e;
}
var Al = bl, wl = Ne;
function xl(t) {
  return wl(this, t).get(t);
}
var Tl = xl, El = Ne;
function Sl(t) {
  return El(this, t).has(t);
}
var jl = Sl, Cl = Ne;
function Ol(t, e) {
  var r = Cl(this, t), n = r.size;
  return r.set(t, e), this.size += r.size == n ? 0 : 1, this;
}
var Pl = Ol, kl = vl, Ml = Al, Il = Tl, Hl = jl, Rl = Pl;
function te(t) {
  var e = -1, r = t == null ? 0 : t.length;
  for (this.clear(); ++e < r; ) {
    var n = t[e];
    this.set(n[0], n[1]);
  }
}
te.prototype.clear = kl;
te.prototype.delete = Ml;
te.prototype.get = Il;
te.prototype.has = Hl;
te.prototype.set = Rl;
var wt = te, Ll = Le, Ul = At, Nl = wt, Dl = 200;
function Bl(t, e) {
  var r = this.__data__;
  if (r instanceof Ll) {
    var n = r.__data__;
    if (!Ul || n.length < Dl - 1)
      return n.push([t, e]), this.size = ++r.size, this;
    r = this.__data__ = new Nl(n);
  }
  return r.set(t, e), this.size = r.size, this;
}
var zl = Bl, Kl = Le, Fl = ro, Gl = io, Vl = so, ql = lo, Wl = zl;
function re(t) {
  var e = this.__data__ = new Kl(t);
  this.size = e.size;
}
re.prototype.clear = Fl;
re.prototype.delete = Gl;
re.prototype.get = Vl;
re.prototype.has = ql;
re.prototype.set = Wl;
var an = re, Yl = "__lodash_hash_undefined__";
function Jl(t) {
  return this.__data__.set(t, Yl), this;
}
var Xl = Jl;
function Zl(t) {
  return this.__data__.has(t);
}
var Ql = Zl, ec = wt, tc = Xl, rc = Ql;
function Oe(t) {
  var e = -1, r = t == null ? 0 : t.length;
  for (this.__data__ = new ec(); ++e < r; )
    this.add(t[e]);
}
Oe.prototype.add = Oe.prototype.push = tc;
Oe.prototype.has = rc;
var nc = Oe;
function ic(t, e) {
  for (var r = -1, n = t == null ? 0 : t.length; ++r < n; )
    if (e(t[r], r, t))
      return !0;
  return !1;
}
var ac = ic;
function sc(t, e) {
  return t.has(e);
}
var oc = sc, lc = nc, cc = ac, dc = oc, hc = 1, uc = 2;
function fc(t, e, r, n, i, a) {
  var s = r & hc, o = t.length, c = e.length;
  if (o != c && !(s && c > o))
    return !1;
  var l = a.get(t), u = a.get(e);
  if (l && u)
    return l == e && u == t;
  var h = -1, d = !0, p = r & uc ? new lc() : void 0;
  for (a.set(t, e), a.set(e, t); ++h < o; ) {
    var f = t[h], v = e[h];
    if (n)
      var m = s ? n(v, f, h, e, t, a) : n(f, v, h, t, e, a);
    if (m !== void 0) {
      if (m)
        continue;
      d = !1;
      break;
    }
    if (p) {
      if (!cc(e, function(j, C) {
        if (!dc(p, C) && (f === j || i(f, j, r, n, a)))
          return p.push(C);
      })) {
        d = !1;
        break;
      }
    } else if (!(f === v || i(f, v, r, n, a))) {
      d = !1;
      break;
    }
  }
  return a.delete(t), a.delete(e), d;
}
var sn = fc, pc = k, vc = pc.Uint8Array, gc = vc;
function yc(t) {
  var e = -1, r = Array(t.size);
  return t.forEach(function(n, i) {
    r[++e] = [i, n];
  }), r;
}
var _c = yc;
function $c(t) {
  var e = -1, r = Array(t.size);
  return t.forEach(function(n) {
    r[++e] = n;
  }), r;
}
var mc = $c, er = He, tr = gc, bc = rn, Ac = sn, wc = _c, xc = mc, Tc = 1, Ec = 2, Sc = "[object Boolean]", jc = "[object Date]", Cc = "[object Error]", Oc = "[object Map]", Pc = "[object Number]", kc = "[object RegExp]", Mc = "[object Set]", Ic = "[object String]", Hc = "[object Symbol]", Rc = "[object ArrayBuffer]", Lc = "[object DataView]", rr = er ? er.prototype : void 0, rt = rr ? rr.valueOf : void 0;
function Uc(t, e, r, n, i, a, s) {
  switch (r) {
    case Lc:
      if (t.byteLength != e.byteLength || t.byteOffset != e.byteOffset)
        return !1;
      t = t.buffer, e = e.buffer;
    case Rc:
      return !(t.byteLength != e.byteLength || !a(new tr(t), new tr(e)));
    case Sc:
    case jc:
    case Pc:
      return bc(+t, +e);
    case Cc:
      return t.name == e.name && t.message == e.message;
    case kc:
    case Ic:
      return t == e + "";
    case Oc:
      var o = wc;
    case Mc:
      var c = n & Tc;
      if (o || (o = xc), t.size != e.size && !c)
        return !1;
      var l = s.get(t);
      if (l)
        return l == e;
      n |= Ec, s.set(t, e);
      var u = Ac(o(t), o(e), n, i, a, s);
      return s.delete(t), u;
    case Hc:
      if (rt)
        return rt.call(t) == rt.call(e);
  }
  return !1;
}
var Nc = Uc;
function Dc(t, e) {
  for (var r = -1, n = e.length, i = t.length; ++r < n; )
    t[i + r] = e[r];
  return t;
}
var Bc = Dc, zc = Bc, Kc = I;
function Fc(t, e, r) {
  var n = e(t);
  return Kc(t) ? n : zc(n, r(t));
}
var Gc = Fc;
function Vc() {
  return [];
}
var qc = Vc, Wc = qr, Yc = qc, Jc = Object.prototype, Xc = Jc.propertyIsEnumerable, nr = Object.getOwnPropertySymbols, Zc = nr ? function(t) {
  return t == null ? [] : (t = Object(t), Wc(nr(t), function(e) {
    return Xc.call(t, e);
  }));
} : Yc, Qc = Zc, ed = Gc, td = Qc, rd = bt;
function nd(t) {
  return ed(t, rd, td);
}
var id = nd, ir = id, ad = 1, sd = Object.prototype, od = sd.hasOwnProperty;
function ld(t, e, r, n, i, a) {
  var s = r & ad, o = ir(t), c = o.length, l = ir(e), u = l.length;
  if (c != u && !s)
    return !1;
  for (var h = c; h--; ) {
    var d = o[h];
    if (!(s ? d in e : od.call(e, d)))
      return !1;
  }
  var p = a.get(t), f = a.get(e);
  if (p && f)
    return p == e && f == t;
  var v = !0;
  a.set(t, e), a.set(e, t);
  for (var m = s; ++h < c; ) {
    d = o[h];
    var j = t[d], C = e[d];
    if (n)
      var be = s ? n(C, j, d, e, t, a) : n(j, C, d, t, e, a);
    if (!(be === void 0 ? j === C || i(j, C, r, n, a) : be)) {
      v = !1;
      break;
    }
    m || (m = d == "constructor");
  }
  if (v && !m) {
    var G = t.constructor, H = e.constructor;
    G != H && "constructor" in t && "constructor" in e && !(typeof G == "function" && G instanceof G && typeof H == "function" && H instanceof H) && (v = !1);
  }
  return a.delete(t), a.delete(e), v;
}
var cd = ld, dd = Q, hd = k, ud = dd(hd, "DataView"), fd = ud, pd = Q, vd = k, gd = pd(vd, "Promise"), yd = gd, _d = Q, $d = k, md = _d($d, "Set"), bd = md, Ad = Q, wd = k, xd = Ad(wd, "WeakMap"), Td = xd, ht = fd, ut = At, ft = yd, pt = bd, vt = Td, on = _e, ne = nn, ar = "[object Map]", Ed = "[object Object]", sr = "[object Promise]", or = "[object Set]", lr = "[object WeakMap]", cr = "[object DataView]", Sd = ne(ht), jd = ne(ut), Cd = ne(ft), Od = ne(pt), Pd = ne(vt), B = on;
(ht && B(new ht(new ArrayBuffer(1))) != cr || ut && B(new ut()) != ar || ft && B(ft.resolve()) != sr || pt && B(new pt()) != or || vt && B(new vt()) != lr) && (B = function(t) {
  var e = on(t), r = e == Ed ? t.constructor : void 0, n = r ? ne(r) : "";
  if (n)
    switch (n) {
      case Sd:
        return cr;
      case jd:
        return ar;
      case Cd:
        return sr;
      case Od:
        return or;
      case Pd:
        return lr;
    }
  return e;
});
var kd = B, nt = an, Md = sn, Id = Nc, Hd = cd, dr = kd, hr = I, ur = Ce.exports, Rd = Qr, Ld = 1, fr = "[object Arguments]", pr = "[object Array]", xe = "[object Object]", Ud = Object.prototype, vr = Ud.hasOwnProperty;
function Nd(t, e, r, n, i, a) {
  var s = hr(t), o = hr(e), c = s ? pr : dr(t), l = o ? pr : dr(e);
  c = c == fr ? xe : c, l = l == fr ? xe : l;
  var u = c == xe, h = l == xe, d = c == l;
  if (d && ur(t)) {
    if (!ur(e))
      return !1;
    s = !0, u = !1;
  }
  if (d && !u)
    return a || (a = new nt()), s || Rd(t) ? Md(t, e, r, n, i, a) : Id(t, e, c, r, n, i, a);
  if (!(r & Ld)) {
    var p = u && vr.call(t, "__wrapped__"), f = h && vr.call(e, "__wrapped__");
    if (p || f) {
      var v = p ? t.value() : t, m = f ? e.value() : e;
      return a || (a = new nt()), i(v, m, r, n, a);
    }
  }
  return d ? (a || (a = new nt()), Hd(t, e, r, n, i, a)) : !1;
}
var Dd = Nd, Bd = Dd, gr = $e;
function ln(t, e, r, n, i) {
  return t === e ? !0 : t == null || e == null || !gr(t) && !gr(e) ? t !== t && e !== e : Bd(t, e, r, n, ln, i);
}
var cn = ln, zd = an, Kd = cn, Fd = 1, Gd = 2;
function Vd(t, e, r, n) {
  var i = r.length, a = i, s = !n;
  if (t == null)
    return !a;
  for (t = Object(t); i--; ) {
    var o = r[i];
    if (s && o[2] ? o[1] !== t[o[0]] : !(o[0] in t))
      return !1;
  }
  for (; ++i < a; ) {
    o = r[i];
    var c = o[0], l = t[c], u = o[1];
    if (s && o[2]) {
      if (l === void 0 && !(c in t))
        return !1;
    } else {
      var h = new zd();
      if (n)
        var d = n(l, u, c, t, e, h);
      if (!(d === void 0 ? Kd(u, l, Fd | Gd, n, h) : d))
        return !1;
    }
  }
  return !0;
}
var qd = Vd, Wd = me;
function Yd(t) {
  return t === t && !Wd(t);
}
var dn = Yd, Jd = dn, Xd = bt;
function Zd(t) {
  for (var e = Xd(t), r = e.length; r--; ) {
    var n = e[r], i = t[n];
    e[r] = [n, i, Jd(i)];
  }
  return e;
}
var Qd = Zd;
function eh(t, e) {
  return function(r) {
    return r == null ? !1 : r[t] === e && (e !== void 0 || t in Object(r));
  };
}
var hn = eh, th = qd, rh = Qd, nh = hn;
function ih(t) {
  var e = rh(t);
  return e.length == 1 && e[0][2] ? nh(e[0][0], e[0][1]) : function(r) {
    return r === t || th(r, t, e);
  };
}
var ah = ih, sh = _e, oh = $e, lh = "[object Symbol]";
function ch(t) {
  return typeof t == "symbol" || oh(t) && sh(t) == lh;
}
var De = ch, dh = I, hh = De, uh = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/, fh = /^\w*$/;
function ph(t, e) {
  if (dh(t))
    return !1;
  var r = typeof t;
  return r == "number" || r == "symbol" || r == "boolean" || t == null || hh(t) ? !0 : fh.test(t) || !uh.test(t) || e != null && t in Object(e);
}
var xt = ph, un = wt, vh = "Expected a function";
function Tt(t, e) {
  if (typeof t != "function" || e != null && typeof e != "function")
    throw new TypeError(vh);
  var r = function() {
    var n = arguments, i = e ? e.apply(this, n) : n[0], a = r.cache;
    if (a.has(i))
      return a.get(i);
    var s = t.apply(this, n);
    return r.cache = a.set(i, s) || a, s;
  };
  return r.cache = new (Tt.Cache || un)(), r;
}
Tt.Cache = un;
var gh = Tt, yh = gh, _h = 500;
function $h(t) {
  var e = yh(t, function(n) {
    return r.size === _h && r.clear(), n;
  }), r = e.cache;
  return e;
}
var mh = $h, bh = mh, Ah = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g, wh = /\\(\\)?/g, xh = bh(function(t) {
  var e = [];
  return t.charCodeAt(0) === 46 && e.push(""), t.replace(Ah, function(r, n, i, a) {
    e.push(i ? a.replace(wh, "$1") : n || r);
  }), e;
}), Th = xh;
function Eh(t, e) {
  for (var r = -1, n = t == null ? 0 : t.length, i = Array(n); ++r < n; )
    i[r] = e(t[r], r, t);
  return i;
}
var Sh = Eh, yr = He, jh = Sh, Ch = I, Oh = De, Ph = 1 / 0, _r = yr ? yr.prototype : void 0, $r = _r ? _r.toString : void 0;
function fn(t) {
  if (typeof t == "string")
    return t;
  if (Ch(t))
    return jh(t, fn) + "";
  if (Oh(t))
    return $r ? $r.call(t) : "";
  var e = t + "";
  return e == "0" && 1 / t == -Ph ? "-0" : e;
}
var kh = fn, Mh = kh;
function Ih(t) {
  return t == null ? "" : Mh(t);
}
var Hh = Ih, Rh = I, Lh = xt, Uh = Th, Nh = Hh;
function Dh(t, e) {
  return Rh(t) ? t : Lh(t, e) ? [t] : Uh(Nh(t));
}
var pn = Dh, Bh = De, zh = 1 / 0;
function Kh(t) {
  if (typeof t == "string" || Bh(t))
    return t;
  var e = t + "";
  return e == "0" && 1 / t == -zh ? "-0" : e;
}
var Be = Kh, Fh = pn, Gh = Be;
function Vh(t, e) {
  e = Fh(e, t);
  for (var r = 0, n = e.length; t != null && r < n; )
    t = t[Gh(e[r++])];
  return r && r == n ? t : void 0;
}
var vn = Vh, qh = vn;
function Wh(t, e, r) {
  var n = t == null ? void 0 : qh(t, e);
  return n === void 0 ? r : n;
}
var Yh = Wh;
function Jh(t, e) {
  return t != null && e in Object(t);
}
var Xh = Jh, Zh = pn, Qh = Xr, eu = I, tu = Zr, ru = mt, nu = Be;
function iu(t, e, r) {
  e = Zh(e, t);
  for (var n = -1, i = e.length, a = !1; ++n < i; ) {
    var s = nu(e[n]);
    if (!(a = t != null && r(t, s)))
      break;
    t = t[s];
  }
  return a || ++n != i ? a : (i = t == null ? 0 : t.length, !!i && ru(i) && tu(s, i) && (eu(t) || Qh(t)));
}
var au = iu, su = Xh, ou = au;
function lu(t, e) {
  return t != null && ou(t, e, su);
}
var cu = lu, du = cn, hu = Yh, uu = cu, fu = xt, pu = dn, vu = hn, gu = Be, yu = 1, _u = 2;
function $u(t, e) {
  return fu(t) && pu(e) ? vu(gu(t), e) : function(r) {
    var n = hu(r, t);
    return n === void 0 && n === e ? uu(r, t) : du(e, n, yu | _u);
  };
}
var mu = $u;
function bu(t) {
  return t;
}
var Au = bu;
function wu(t) {
  return function(e) {
    return e == null ? void 0 : e[t];
  };
}
var xu = wu, Tu = vn;
function Eu(t) {
  return function(e) {
    return Tu(e, t);
  };
}
var Su = Eu, ju = xu, Cu = Su, Ou = xt, Pu = Be;
function ku(t) {
  return Ou(t) ? ju(Pu(t)) : Cu(t);
}
var Mu = ku, Iu = ah, Hu = mu, Ru = Au, Lu = I, Uu = Mu;
function Nu(t) {
  return typeof t == "function" ? t : t == null ? Ru : typeof t == "object" ? Lu(t) ? Hu(t[0], t[1]) : Iu(t) : Uu(t);
}
var Du = Nu, Bu = "Expected a function";
function zu(t) {
  if (typeof t != "function")
    throw new TypeError(Bu);
  return function() {
    var e = arguments;
    switch (e.length) {
      case 0:
        return !t.call(this);
      case 1:
        return !t.call(this, e[0]);
      case 2:
        return !t.call(this, e[0], e[1]);
      case 3:
        return !t.call(this, e[0], e[1], e[2]);
    }
    return !t.apply(this, e);
  };
}
var Ku = zu, Fu = qr, Gu = Cs, Vu = Du, qu = I, Wu = Ku;
function Yu(t, e) {
  var r = qu(t) ? Fu : Gu;
  return r(t, Wu(Vu(e)));
}
var Et = Yu, Ju = k, Xu = function() {
  return Ju.Date.now();
}, Zu = Xu, Qu = /\s/;
function ef(t) {
  for (var e = t.length; e-- && Qu.test(t.charAt(e)); )
    ;
  return e;
}
var tf = ef, rf = tf, nf = /^\s+/;
function af(t) {
  return t && t.slice(0, rf(t) + 1).replace(nf, "");
}
var sf = af, of = sf, mr = me, lf = De, br = 0 / 0, cf = /^[-+]0x[0-9a-f]+$/i, df = /^0b[01]+$/i, hf = /^0o[0-7]+$/i, uf = parseInt;
function ff(t) {
  if (typeof t == "number")
    return t;
  if (lf(t))
    return br;
  if (mr(t)) {
    var e = typeof t.valueOf == "function" ? t.valueOf() : t;
    t = mr(e) ? e + "" : e;
  }
  if (typeof t != "string")
    return t === 0 ? t : +t;
  t = of(t);
  var r = df.test(t);
  return r || hf.test(t) ? uf(t.slice(2), r ? 2 : 8) : cf.test(t) ? br : +t;
}
var pf = ff, vf = me, it = Zu, Ar = pf, gf = "Expected a function", yf = Math.max, _f = Math.min;
function $f(t, e, r) {
  var n, i, a, s, o, c, l = 0, u = !1, h = !1, d = !0;
  if (typeof t != "function")
    throw new TypeError(gf);
  e = Ar(e) || 0, vf(r) && (u = !!r.leading, h = "maxWait" in r, a = h ? yf(Ar(r.maxWait) || 0, e) : a, d = "trailing" in r ? !!r.trailing : d);
  function p(x) {
    var R = n, ie = i;
    return n = i = void 0, l = x, s = t.apply(ie, R), s;
  }
  function f(x) {
    return l = x, o = setTimeout(j, e), u ? p(x) : s;
  }
  function v(x) {
    var R = x - c, ie = x - l, St = e - R;
    return h ? _f(St, a - ie) : St;
  }
  function m(x) {
    var R = x - c, ie = x - l;
    return c === void 0 || R >= e || R < 0 || h && ie >= a;
  }
  function j() {
    var x = it();
    if (m(x))
      return C(x);
    o = setTimeout(j, v(x));
  }
  function C(x) {
    return o = void 0, d && n ? p(x) : (n = i = void 0, s);
  }
  function be() {
    o !== void 0 && clearTimeout(o), l = 0, n = c = i = o = void 0;
  }
  function G() {
    return o === void 0 ? s : C(it());
  }
  function H() {
    var x = it(), R = m(x);
    if (n = arguments, i = this, c = x, R) {
      if (o === void 0)
        return f(c);
      if (h)
        return clearTimeout(o), o = setTimeout(j, e), p(c);
    }
    return o === void 0 && (o = setTimeout(j, e)), s;
  }
  return H.cancel = be, H.flush = G, H;
}
var mf = $f;
const bf = (t) => ({
  onSearch: e,
  debounce: r,
  action: n
}) => {
  let i = /* @__PURE__ */ new Set();
  const a = () => {
    t.data = Et(t.data, (l) => i.has(l.id));
    for (const l of i)
      ei(l);
    i = /* @__PURE__ */ new Set();
  }, s = (l) => {
    l.forEach((u) => {
      i.add(u.id), Gr(u);
    }), t.data = [
      ...t.data,
      ...$t(l).map((u) => ({
        ...u,
        parent: n.id
      }))
    ];
  }, o = mf(async ({ detail: l }) => {
    const { search: u } = l;
    a();
    const h = await e(u);
    !h || s(h);
  }, r), c = () => (t.addEventListener("change", o), t.open({ parent: n.id }), { keepOpen: !0 });
  return t.data = [
    ...t.data,
    {
      ...n,
      handler: c
    }
  ], s;
};
function Af({
  params: t
}) {
  t && Xn(t);
  const e = document.createElement("ninja-keys");
  return e.style.setProperty("--ninja-accent-color", "#068466"), e.style.setProperty("", "1200"), document.body.append(e), ii(e), { asyncActionBuilder: bf(e) };
}
const wf = (...t) => {
  const e = document.querySelector("ninja-keys");
  if (!e)
    return console.warn("no ninja keys"), Vr;
  const r = new Set(t.map((n) => n.id));
  return e.data = [...e.data, ...t], () => e.data = Et(e.data, (n) => r.has(n.id));
}, xf = (...t) => {
  const e = document.querySelector("ninja-keys");
  if (!e)
    return console.warn("no ninja keys"), Vr;
  const r = new Set(t.map((n) => n.id));
  return e.data = [...e.data, ...$t(t)], () => e.data = Et(e.data, (n) => r.has(n.id));
};
window.commandPalette = {
  register: wf,
  setup: Af,
  registerNav: xf
};
export {
  wf as register,
  xf as registerNav,
  Af as setup
};
